/**
 * QuoteBuilder — the tech side of Good/Better/Best (lives on the service call, after
 * diagnosis: build the Best/Better/Good collections, then Present to the customer; an
 * accepted repair → Complete & Invoice, an accepted replacement → a sold Job). First
 * visible slice: 3 brandable collection cards + equipment slots that pull straight from
 * the pricebook (photo + model + price) + live per-tier totals.
 */
import { useBackTarget } from "@/lib/navBack";
import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  MoreHorizontal, ArrowLeft, ArrowRight, Plus, X, Search, ExternalLink, Trash2, Flame, Snowflake, Wind, Settings2, Copy, Check, CopyPlus,
  Eye, CheckCircle2, Clock, Send, XCircle, FileEdit, Activity, Droplet, Wrench, Package, Thermometer, Zap, Plug, Star,
  ShieldCheck, Scale, Minus, CalendarClock, Stethoscope, FlaskConical, Boxes,
  MessageSquare, Presentation, Mail, ChevronDown, Reply, Lock, Pencil, RefreshCw, Users,
} from "lucide-react";
import { useCurrentUser } from "@/lib/currentUser";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useEquipmentItems, isSyntheticPart, fetchItemById, type Item, sellPriceOf } from "@/features/items/items";
import { PricebookPicker } from "@/features/items/PricebookPicker";
import { usePlanPriceCatalog, repairPctForTerm } from "@/features/agreements/planPricing";
import { usePlanDiscounts, useFeeItems } from "@/features/customer-360/serviceTypes";
import { memberDiscountByRule } from "@/lib/memberPricing";
import { usePlanCatalog } from "@/features/agreements/PlanBuilder";
import { useCustomerEquipment } from "@/features/customer-360/CustomerEquipment";
import { useEquipmentOverrides } from "@/features/customer-360/equipmentOverrides";
import { planCoversFor, planSystemsCount, planSystemsMultiplier, mergeEquipOverrides, makeFromMfg, type PlanOfferCover } from "@/lib/planCovers";
import { buildEquipmentView, installDateInfo, type ClassifiedEquip } from "@/lib/equipment";
import { UnitPickRow } from "@/components/UnitPickRow";
import { InfoTip } from "@/components/InfoTip";
import { useCustomer } from "@/features/customer-360/api";
import { displayCustomerName } from "@/lib/format";
import { isAutoTitle, autoQuoteTitle } from "./quoteTitle";
import {
  useQuote, useAddQuoteLine, useRemoveQuoteLine, useUpdateQuoteLine, useSetOptionBrand, useUpdateQuoteOption,
  useAddQuoteDiscount, useUpdateQuoteDiscount, useRemoveQuoteDiscount, useSetQuoteStatus, useQuoteEvents, useCloneQuote,
  useUpdateQuote, useAddQuoteOption, useRemoveQuoteOption, useDiagnosticFees, useCustomerMembershipStatus, useRepairReplace,
  useAcceptOption, useDeleteQuote, useQuoteJobExists, useBookRepairVisit, useExtendQuote, quoteExpiry,
  useQuoteTemplates, useSaveAsTemplate, useApplyTemplate, useDeleteTemplate, useRenameTemplate, useReplaceTemplate, useShareTemplate, useApplyQuotePackage, useCopyOptionExtras,
  useQuoteMessages, useMarkMessageReplied, useRecordQuoteReply, lineWarranty, lineWarrantyLabel,
  useAddQuoteRebate, useUpdateQuoteRebate, useRemoveQuoteRebate,
  optionTotal, optionSubtotal, lineTotal, resolveDiscount,
  QUOTE_STATUSES, QUOTE_STATUS_LABEL,
  type QuoteOptionFull, type QuoteLine, type Quote, type QuoteTier, type QuoteTemplate,
} from "./quotes";
import { useFinancingPlans, useUtilities, estMonthly, useQuoteBrands, useQuoteCategories, useQuotePackages, pkgTotal, type QuoteCategory, type QuotePackage, type Utility } from "./quotesAdmin";
import { useCreateJobFromQuote } from "@/features/jobs/jobs";
import { SalePaymentLine, saleChoice } from "./SalePaymentLine";
// QUOTE-POLISH-03: reply-to-homeowner reuses the 360's composers.
import { ComposeTextModal, ComposeEmailModal } from "@/features/customer-360/CustomerMessages";
// PROPOSAL-FLOW-02: sending moved to the customer view's staff bar (Review &
// Present → Send Link); the builder keeps the notes convention from there.
import { splitOfficeNote, joinOfficeNote, proposalUrlFor, copyText } from "./proposalSend";
// SIZE-ASSIST-01: county-records sizing card + right-size highlighting in the picker.
import { SizingCard, useSizingAssist, familyHint, isRightSize, type SizingSuggestion } from "./SizingAssist";

const money = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

// Best wears the brand blue (the hero), Better/Good wear near-black — like the proposal app.
const TIER_HEAD: Record<string, string> = {
  best: "bg-brand-700 text-white",
  better: "bg-[#1c1a16] text-white",
  good: "bg-[#1c1a16] text-white",
};

// Category icons live in the catalog as a lucide name string; map it to the component.
const CAT_ICONS: Record<string, typeof Flame> = {
  Flame, Snowflake, Wind, Settings2, Droplet, Wrench, Package, Thermometer, Zap, Plug,
};
function catIcon(name?: string | null): typeof Flame {
  return (name && CAT_ICONS[name]) || Package;
}

/**
 * Which category slots a quote shows. An explicit per-quote set wins (single-offering /
 * "just a water heater"); otherwise the catalog defaults for this quote's type (install
 * shows furnace/AC/heat-pump/accessories; service shows repair + accessories). Returns the
 * categories themselves, in catalog sort order.
 */
function shownCategories(quote: Quote, cats: QuoteCategory[]): QuoteCategory[] {
  const active = cats.filter((c) => c.is_active && !c.parent_key); // top-level slots only
  if (quote.category_keys && quote.category_keys.length) {
    const set = new Set(quote.category_keys);
    return active.filter((c) => set.has(c.key));
  }
  const qt = quote.quote_type || "install";
  return active.filter((c) => c.default_on && (c.applies_to === "both" || c.applies_to === qt));
}

/** Per-option slots: an option's own scope wins (the three-scope quick-start sets these —
 * Repair column shows repair components, Replace column shows equipment), else the
 * quote-level slots. */
function shownCategoriesForOption(quote: Quote, option: QuoteOptionFull, cats: QuoteCategory[]): QuoteCategory[] {
  if (option.category_keys && option.category_keys.length) {
    const set = new Set(option.category_keys);
    return cats.filter((c) => c.is_active && !c.parent_key && set.has(c.key));
  }
  return shownCategories(quote, cats);
}

type PickerState = {
  optionId: string;
  kind: string;
  pricebookCategory: string | null;
  label: string;
  isEquipment: boolean;
  brand: string | null;
  subcategory: string | null;
  category: string | null;
  /** GBB-ORDER-01: the collection's tier — its equipment ranks first in the picker. */
  optionTier: QuoteTier | null;
  /** QUOTE-POLISH-03: accessories picked from a slot are agreed-to gear — they
   *  land Included · $0 (value shown struck). Equipment is always priced. */
  inc: boolean;
};
/** A sub-card's filter: which item column to match and the value (set by pricebook_match). */
type SubPick = { matchField: string; value: string | null; label: string };

export function QuoteBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useQuote(id ?? null);
  const allCats = useQuoteCategories();
  const updateQuote = useUpdateQuote();
  const createJob = useCreateJobFromQuote();
  // QUOTE-FIX-01: manual-accept option chooser + a health check on the job
  // link (a deleted job used to leave this quote saying "job not found").
  const acceptOption = useAcceptOption();
  const jobExists = useQuoteJobExists(data?.quote.job_id ?? null);
  const [picker, setPicker] = useState<PickerState | null>(null);
  // QUOTE-POLISH-11 (tablet / phone): below `lg` the three collections don't
  // fit side by side, so a Best | Better | Good tab bar shows ONE at a time.
  // Desktop keeps the three-up canvas; nothing else changes.
  const [activeOptId, setActiveOptId] = useState<string | null>(null);
  // SIZE-ASSIST-01: computed once here so the card AND the picker share it
  // (react-query dedupes the underlying fetches by key).
  const sizing = useSizingAssist(data?.quote ?? null);

  const cats = useMemo(
    () => (data ? shownCategories(data.quote, allCats.data ?? []) : []),
    [data, allCats.data],
  );
  // BUILDER-FLOW-01: the property's units (Working on / Replacing rows, the auto name), the
  // customer's name and address for the header, and the name the quote gives itself.
  const units = usePropertyUnits(data?.quote.custno ?? null, data?.quote.locno ?? null);
  const cust = useCustomer(data?.quote.custno ?? undefined).data;
  const loc = cust?.locations.find((l) => l.locno === data?.quote.locno) ?? cust?.locations[0] ?? null;
  const addOption = useAddQuoteOption();
  const [planExpanded, setPlanExpanded] = useState(false);
  const [addOnsOpen, setAddOnsOpen] = useState(false);
  const optionsSig = (data?.options ?? []).map((o) => `${o.id}:${o.brand ?? ""}:${o.lines.map((l) => `${l.kind}/${l.model ?? ""}/${l.manufacturer ?? ""}`).join(",")}`).join("|");
  const unitsSig = units.map((u) => u.counter).join(",");
  const q = data?.quote;
  useEffect(() => {
    if (!q || !isAutoTitle(q)) return;
    if (q.status === "accepted" || q.status === "declined") return;
    const t = autoQuoteTitle({
      quote: q, options: data?.options ?? [], units,
      kindLabel: (k) => (allCats.data ?? []).find((c) => c.key === k)?.label ?? null,
      address: loc?.add1 ?? null,
    });
    if (t && t !== (q.title ?? "")) updateQuote.mutate({ id: q.id, title: t, title_is_auto: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q?.id, q?.title, q?.title_is_auto, q?.status, JSON.stringify(q?.work_units ?? null), q?.recommended_option_id, optionsSig, unitsSig, loc?.add1, allCats.data?.length]);

  if (isLoading) return <div className="p-8 text-sm text-slate-500">Loading quote…</div>;
  if (!data) return <div className="p-8 text-sm text-slate-500">Quote not found.</div>;

  const acceptedOpt = data.quote.accepted_option_id ? data.options.find((o) => o.id === data.quote.accepted_option_id) : null;
  const isService = (data.quote.quote_type || "install") === "service";
  // QUOTE-LIFECYCLE-01: accepted/declined/expired proposals are READ-ONLY —
  // the signed (or dead) deal stays exactly as the customer saw it. Changes
  // after acceptance are change orders on the job; a new deal is a Re-bid.
  const exp = quoteExpiry(data.quote);
  const locked = data.quote.status === "accepted" || data.quote.status === "declined" || exp.expired;
  // The tab that's showing on a tablet/phone: what was tapped, else the
  // advisor's pick, else the first collection.
  const activeId = (activeOptId && data.options.some((o) => o.id === activeOptId) ? activeOptId : null)
    ?? data.options.find((o) => o.id === data.quote.recommended_option_id)?.id
    ?? data.options[0]?.id
    ?? null;

  const openPicker = (o: QuoteOptionFull, cat: QuoteCategory, sub?: SubPick | null) =>
    setPicker({
      optionId: o.id,
      kind: cat.key,
      pricebookCategory: cat.pricebook_category,
      label: sub?.label ?? cat.label,
      isEquipment: cat.kind === "equipment",
      brand: o.brand,
      subcategory: sub && sub.matchField !== "category" ? sub.value : null,
      category: sub && sub.matchField === "category" ? sub.value : null,
      optionTier: cat.kind === "equipment" ? o.tier : null,
      inc: cat.kind !== "equipment",
    });

  const custName = cust ? displayCustomerName(null, cust.fullname) : null;
  const where = [loc ? [loc.add1, loc.city].filter(Boolean).join(", ") : null, data.quote.dispatch_no ? `call #${data.quote.dispatch_no}` : null].filter(Boolean).join(" · ");
  const hasLines = data.options.some((o) => o.lines.length > 0);
  const tipWorking = isService
    ? "The unit this repair is about. The tech's answer to “What are you working on?” lands here on its own; tap to change it. The homeowner's page leads with it."
    : "What the new system replaces, from the property's equipment record. Tap to change it.";
  const tipMain = isService
    ? "Parts, labor and fees for the repair. A second option (fix vs. fix + more) is a tab; the homeowner picks one. Equipment replacements belong on a System proposal."
    : "Good / Better / Best. Each package is its own column — equipment, included accessories, discounts and rebates. Packages ▾ fills a column from the shop's packages; Favorites fills all three from your own.";
  const tipPlan = "The maintenance plan offered with this proposal — one line here, a card on the homeowner's page. They tick it and pick a term; it activates when the invoice is paid.";
  const tipSend = "Review & Present opens the homeowner's page exactly as they see it. Send the link, present in person, or record the decision from the bar at the bottom of that page. Notes: the homeowner's shows on their page; the office's never does.";

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6">
      {/* BUILDER-FLOW-01 (Seth 2026-10-30): back · kind · the quote's own name (click to rename) ·
          #reference · where · the status word · one ⋯ menu. Nothing explains itself here — the (i)s do. */}
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 transition-colors hover:text-brand-700">
              <ArrowLeft className="h-3.5 w-3.5" /> {custName ?? "Back"}
            </button>
            <span className={cn("rounded px-1.5 py-px text-[10px] font-extrabold uppercase tracking-wide", isService ? "bg-orange-50 text-orange-700" : "bg-sky-50 text-sky-700")}>{isService ? "Repair" : "System"}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <QuoteTitle quote={data.quote} locked={locked} />
            <span className="font-mono text-xs text-slate-400">#{data.quote.id.slice(0, 8).toUpperCase()}</span>
          </div>
          {where && <p className="mt-0.5 text-sm text-slate-500">{where}</p>}
          {/* QUOTE-FIX-01: a MANUAL accept (status dropdown) doesn't say which
              option won — ask, right here, so the job flow can light up. */}
          {data.quote.status === "accepted" && !acceptedOpt && data.options.length > 0 && (
            <span className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-emerald-50 px-3.5 py-2.5 ring-1 ring-emerald-200">
              <span className="text-sm font-bold text-emerald-800">Marked accepted — which option did they take?</span>
              {data.options.map((o) => (
                <button key={o.id} type="button" disabled={acceptOption.isPending}
                  onClick={() => acceptOption.mutate({ quoteId: data.quote.id, optionId: o.id })}
                  className="rounded-lg bg-white px-3 py-1.5 text-xs font-bold text-emerald-700 ring-1 ring-emerald-300 transition-colors hover:bg-emerald-100">
                  {(o.label || o.tier)} · {money(optionTotal(o))}
                </button>
              ))}
            </span>
          )}
          {data.quote.status === "accepted" && acceptedOpt && (
            <span className="mt-2 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
                <Check className="h-3.5 w-3.5" /> Customer accepted the {acceptedOpt.label || acceptedOpt.tier} option
              </span>
              {/* PLAN-SHOW-01: pay in full, or the exact financing plan to process. */}
              {saleChoice(data.quote) && (
                <SalePaymentLine quote={data.quote} className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200" />
              )}
              {/* QUOTE→JOB: the accepted proposal becomes the install job in
                  one click — price + title carry over, nothing re-typed.
                  A DELETED job (jobExists false) re-opens the create path.
                  CALLBACK-01: an accepted REPAIR books a return visit instead. */}
              {isService ? (
                <BookRepairCta quote={data.quote} option={acceptedOpt} />
              ) : data.quote.job_id && jobExists.data !== false ? (
                <button
                  type="button"
                  onClick={() => navigate(`/jobs/${data.quote.job_id}`)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-xs font-bold text-brand-700 transition-colors hover:bg-brand-100"
                >
                  Open the install job <ArrowRight className="h-3.5 w-3.5" />
                </button>
              ) : (
                <>
                {data.quote.job_id && jobExists.data === false && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-200">
                    The job this created was deleted — create it again below
                  </span>
                )}
                <button
                  type="button"
                  disabled={createJob.isPending || !data.quote.custno}
                  title={!data.quote.custno ? "Attach a customer to the quote first" : undefined}
                  onClick={() =>
                    // QUOTE-JOB-ONE-01: the accepted option's units, price and
                    // title carry over in the database — sold it once, never re-typed.
                    createJob.mutate(
                      { quoteId: data.quote.id },
                      { onSuccess: (job) => navigate(`/jobs/${job.id}`) },
                    )
                  }
                  className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-60"
                >
                  {createJob.isPending ? "Creating…" : "Create the install job"}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
                </>
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2.5">
          <span className="text-xs font-semibold text-slate-400">{QUOTE_STATUS_LABEL[data.quote.status as (typeof QUOTE_STATUSES)[number]] ?? data.quote.status}{data.quote.is_practice ? " · practice" : ""}</span>
          <QuoteActions
            parts="menu"
            quote={data.quote}
            locked={locked}
            hasContent={hasLines}
            practiceOn={!!data.quote.is_practice}
            onTogglePractice={() => updateQuote.mutate({ id: data.quote.id, is_practice: !data.quote.is_practice })}
          />
        </div>
      </header>

      {/* DESIGN-QUIET-02: a state banner is a noun and an (i); the why lives under the (i). */}
      {data.quote.is_practice && (
        <div className="mb-4 flex items-center gap-2 rounded-xl bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-800 ring-1 ring-violet-200">
          <FlaskConical className="h-4 w-4 shrink-0" />
          Practice build
          <InfoTip label="About practice builds">A practice build never shows on Proposals, the 360, or the office lists. Flip Practice off in the ··· menu to make it real.</InfoTip>
        </div>
      )}

      {locked && (
        <div className={cn(
          "mb-4 flex flex-wrap items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ring-1",
          data.quote.status === "accepted" ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
            : data.quote.status === "declined" ? "bg-rose-50 text-rose-700 ring-rose-200"
            : "bg-amber-50 text-amber-800 ring-amber-200",
        )}>
          <Lock className="h-4 w-4 shrink-0" />
          {data.quote.status === "accepted" ? (
            <>Accepted · locked
              <InfoTip label="Why this proposal is locked">Locked so the deal stays exactly what the customer signed. Additions or price changes happen as change orders on the job (installs) or on the repair ticket before booking.</InfoTip></>
          ) : data.quote.status === "declined" ? (
            <>Declined · locked
              <InfoTip label="Why this proposal is locked">Kept as a record. Re-bid clones it into a fresh draft.</InfoTip></>
          ) : (
            <>Expired{exp.expiresAt ? ` ${exp.expiresAt.toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""} · locked
              <InfoTip label="Why this proposal is locked">The customer can no longer accept it. Extend gives them more time at the same price; Re-bid starts over at current pricing.</InfoTip></>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px] lg:items-start">
      <div className={cn("min-w-0 space-y-4", locked && "pointer-events-none select-none opacity-70")}>

        {/* 1 · Working on / Replacing */}
        <Section n={1} title={isService ? "Working on" : "Replacing"} tip={tipWorking} done={(data.quote.work_units ?? []).length > 0}
          right={!isService && cust ? <span className="text-xs text-slate-400">{units.length} on record</span> : null}>
          <WorkingOnRows quote={data.quote} locked={locked} units={units} tone={isService ? "work" : "plan"} />
          {!isService && <div className="mt-3"><SizingCard quote={data.quote} locked={locked} /></div>}
        </Section>

        {/* 2 · The repair / Packages */}
        <Section n={2} title={isService ? "The repair" : "Packages"} tip={tipMain} done={hasLines}
          right={isService ? (
            <>
              <DiagnosticAdder options={data.options} />
              <button type="button" disabled={addOption.isPending}
                onClick={() => addOption.mutate({ quote_id: data.quote.id, tier: "good", label: `Option ${data.options.length + 1}`, sort: data.options.length })}
                className="text-xs font-semibold text-brand-700 hover:text-brand-900">+ Option</button>
            </>
          ) : (
            <>
              <QuoteActions parts="favorites" quote={data.quote} locked={locked} hasContent={hasLines} />
              <PackageShelf options={data.options} locked={locked} sizing={sizing.suggestion} />
              <LayoutMenu quote={data.quote} options={data.options} allCats={allCats.data ?? []} shown={cats} />
            </>
          )}>
          {isService ? (
            <RepairBuilder quote={data.quote} options={data.options} allCats={allCats.data ?? []} />
          ) : (
          <>
          {/* QUOTE-POLISH-11: tablet / phone tier tabs — one collection at a time
              below `lg`; each tab carries the running total so the comparison
              survives the narrower screen. Hidden on desktop. */}
          {data.options.length >= 2 && (
            <div role="tablist" aria-label="Collections" className="sticky top-2 z-10 mb-3 flex gap-1 rounded-2xl border border-hairline bg-white/95 p-1 shadow-e1 backdrop-blur lg:hidden">
              {data.options.map((o) => {
                const on = o.id === activeId;
                const rec = data.quote.recommended_option_id === o.id;
                const unitCount = o.lines.filter((l) => l.role !== "recommended" && (allCats.data ?? []).some((c) => c.key === l.kind && c.kind === "equipment")).length;
                return (
                  <button
                    key={o.id}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => setActiveOptId(o.id)}
                    className={cn("min-w-0 flex-1 rounded-xl px-2.5 py-2 text-left transition-colors", on ? "bg-slate-900 text-white shadow-e1" : "text-slate-600 hover:bg-slate-50")}
                  >
                    <span className={cn("flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.1em]", on ? "text-white/70" : "text-slate-400")}>
                      {o.label ?? o.tier}{rec && <Star className={cn("h-3 w-3", on ? "fill-amber-300 text-amber-300" : "fill-amber-400 text-amber-400")} />}
                    </span>
                    <span className="block truncate text-[15px] font-bold tabular-nums">{o.lines.length ? money(optionTotal(o)) : "—"}</span>
                    <span className={cn("block truncate text-[10.5px]", on ? "text-white/60" : "text-slate-400")}>
                      {o.lines.length === 0 ? "Empty" : `${unitCount} unit${unitCount === 1 ? "" : "s"}${o.brand ? ` · ${o.brand}` : ""}`}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <div className={cn("grid gap-4", data.options.length >= 3 ? "lg:grid-cols-3" : data.options.length === 2 ? "lg:grid-cols-2" : "max-w-md")}>
            {data.options.map((o) => (
              <div key={o.id} className={cn("min-w-0", data.options.length >= 2 && o.id !== activeId && "hidden lg:block")}>
              <CollectionCard
                o={o}
                quote={data.quote}
                cats={shownCategoriesForOption(data.quote, o, allCats.data ?? [])}
                allCats={allCats.data ?? []}
                siblings={data.options}
                recommended={data.quote.recommended_option_id === o.id}
                onToggleRecommend={() => updateQuote.mutate({ id: data.quote.id, recommended_option_id: data.quote.recommended_option_id === o.id ? null : o.id })}
                onAdd={(cat, sub) => openPicker(o, cat, sub)}
              />
              </div>
            ))}
          </div>
          {/* GBB-GALLERY-01: the accessory catalog as tap-to-toggle tiles — behind one link. */}
          <div className="mt-3">
            <button type="button" onClick={() => setAddOnsOpen((v) => !v)} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:text-brand-900">
              <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", addOnsOpen && "rotate-180")} /> Recommended add-ons
            </button>
            {addOnsOpen && <div className="mt-2"><AddOnGallery quote={data.quote} options={data.options} allCats={allCats.data ?? []} /></div>}
          </div>
          </>
          )}
        </Section>

        {/* 3 · Maintenance plan — one line; Change opens the details. */}
        <Section n={3} title="Maintenance plan" tip={tipPlan} done={!!data.quote.plan_offer_base_id}>
          <PlanOfferBlock quote={data.quote} locked={locked} expanded={planExpanded} onExpand={setPlanExpanded} />
        </Section>

        {/* 4 · Present or send */}
        <Section n={4} title="Present or send" tip={tipSend} done={data.quote.status !== "draft"}>
          <NotesRow quote={data.quote} />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <QuoteActions parts="send" quote={data.quote} locked={locked} hasContent={hasLines} />
          </div>
        </Section>
      </div>

      <BuilderRail quote={data.quote} options={data.options} isService={isService} activeId={activeId} />
      </div>

      <QuoteActivity quoteId={data.quote.id} custno={data.quote.custno} views={{ count: data.quote.view_count ?? 0, last: data.quote.last_viewed_at ?? null }} />

      {picker && (
        <EquipmentPicker
          kind={picker.kind}
          optionId={picker.optionId}
          pricebookCategory={picker.pricebookCategory}
          label={picker.label}
          isEquipment={picker.isEquipment}
          brand={picker.brand}
          subcategory={picker.subcategory}
          category={picker.category}
          optionTier={picker.optionTier}
          inc={picker.inc}
          sizing={sizing.suggestion}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}

/** BUILDER-FLOW-01: one numbered section of the builder — the number, the title, the (i), the body. */
function Section({ n, title, tip, done, right, children }: { n: number; title: string; tip: string; done?: boolean; right?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-hairline bg-white">
      <div className="flex flex-wrap items-center gap-2.5 border-b border-slate-100 px-4 py-3">
        <span className={cn("grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-extrabold text-white", done ? "bg-emerald-600" : "bg-slate-900")}>
          {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : n}
        </span>
        <span className="text-[15px] font-extrabold text-slate-900 [font-family:var(--font-display)]">{title}</span>
        <InfoTip label={`About ${title.toLowerCase()}`}>{tip}</InfoTip>
        {right && <span className="ml-auto flex flex-wrap items-center gap-3">{right}</span>}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

/** The quote's name — click to rename; a typed name stays (title_is_auto = false). */
function QuoteTitle({ quote, locked }: { quote: Quote; locked: boolean }) {
  const update = useUpdateQuote();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(quote.title ?? "");
  useEffect(() => { if (!editing) setVal(quote.title ?? ""); }, [quote.title, editing]);
  const commit = () => {
    const v = val.trim();
    setEditing(false);
    if (v && v !== (quote.title ?? "")) update.mutate({ id: quote.id, title: v, title_is_auto: false });
  };
  if (editing) {
    return (
      <input
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setVal(quote.title ?? ""); setEditing(false); } }}
        aria-label="Quote name"
        className="min-w-[16rem] rounded-lg border border-brand-400 bg-white px-2 py-0.5 text-2xl font-bold tracking-tight text-slate-900 focus:outline-none sm:text-3xl"
        style={{ width: `${Math.min(60, Math.max(16, val.length + 2))}ch` }}
      />
    );
  }
  return (
    <button type="button" disabled={locked} onClick={() => setEditing(true)} title={locked ? undefined : "Rename"}
      className="group -mx-1 inline-flex max-w-full items-center gap-2 rounded-lg px-1 text-left text-2xl font-bold tracking-tight text-slate-900 transition-colors hover:bg-slate-100/80 disabled:hover:bg-transparent sm:text-3xl">
      <span className="truncate">{quote.title || "New quote"}</span>
      {!locked && <Pencil className="h-4 w-4 shrink-0 text-slate-300 opacity-0 transition-opacity group-hover:opacity-100" />}
    </button>
  );
}

/** The property's units, classified the way every screen classifies them (one location at a time). */
function usePropertyUnits(custno: string | null, locno: string | null): ClassifiedEquip[] {
  const equipRows = useCustomerEquipment(custno ?? undefined, locno ?? null).data;
  const equipOverrides = useEquipmentOverrides(custno ?? undefined, locno ?? null).data;
  return useMemo(() => {
    let rows = equipRows ?? [];
    const locs = [...new Set(rows.map((r) => (r as { locno?: string | null }).locno ?? ""))].filter(Boolean).sort();
    if (locs.length > 1) rows = rows.filter((r) => ((r as { locno?: string | null }).locno ?? "") === locs[0]);
    const view = buildEquipmentView(mergeEquipOverrides(rows, equipOverrides), undefined, equipOverrides ?? undefined);
    return [...view.systems, ...view.auxiliary].filter((u) => !u.replaced && !u.duplicateOf);
  }, [equipRows, equipOverrides]);
}

/** The price a plan offer shows right now (the catalog × the systems it covers). Used by the plan line and the rail. */
function usePlanOfferSummary(quote: Quote): { on: boolean; baseName: string | null; term: number; systems: number; price: number | null } {
  const catalog = usePlanPriceCatalog();
  const planCatalog = usePlanCatalog();
  const equipRows = useCustomerEquipment(quote.custno ?? undefined, quote.locno ?? null).data;
  const equipOverrides = useEquipmentOverrides(quote.custno ?? undefined, quote.locno ?? null).data;
  const bases = (catalog.data ?? []).filter((p) => p.kind === "base" && p.active);
  const base = bases.find((b) => b.id === quote.plan_offer_base_id) ?? null;
  const terms = quote.plan_offer_terms && quote.plan_offer_terms.length ? quote.plan_offer_terms : PLAN_TERMS_DEFAULT;
  const term = quote.plan_offer_default_term ?? terms[0] ?? 1;
  const covers = useMemo(() => planCoversFor(equipRows, equipOverrides, quote.plan_offer_covers ?? null), [equipRows, equipOverrides, quote.plan_offer_covers]);
  const systems = Math.max(1, quote.plan_offer_systems ?? planSystemsCount(covers));
  const secondDisc = Number(planCatalog.data?.config?.second_system_discount ?? 0.3);
  const mult = planSystemsMultiplier(systems, secondDisc);
  const p = base ? ({ 1: base.p1, 2: base.p2, 3: base.p3, 5: base.p5, 10: base.p10 } as Record<number, number | null | undefined>)[term] : null;
  const base1 = p ?? (base?.p1 != null ? base.p1 * term : null);
  const freeYear = quote.plan_offer_bundle === "first_year_free";
  return { on: !!quote.plan_offer_base_id, baseName: base?.name ?? null, term, systems, price: freeYear ? 0 : base1 == null ? null : Math.round(base1 * mult * 100) / 100 };
}

/** BUILDER-FLOW-01: the right rail — the proposal as the homeowner will see it, and the customer facts
 *  that matter while pricing (their plan, their repair history). Numbers only. */
function BuilderRail({ quote, options, isService, activeId }: { quote: Quote; options: QuoteOptionFull[]; isService: boolean; activeId: string | null }) {
  const plan = usePlanOfferSummary(quote);
  const status = useCustomerMembershipStatus(quote.custno);
  const rr = useRepairReplace(quote.custno, quote.locno);
  const fees = useFeeItems().data;
  const tiers = usePlanDiscounts().data;
  const sorted = [...options].sort((a, b) => a.sort - b.sort);
  const focus = sorted.find((o) => o.id === quote.recommended_option_id) ?? sorted.find((o) => o.id === activeId) ?? sorted[0] ?? null;
  const st = status.data;
  const d = rr.data;
  const planLine = plan.on && plan.price != null ? plan.price : null;
  // On a repair with the plan ticked, the homeowner's page prices a member's bill (PLAN-DISC-SCOPE-01):
  // the member diagnostic and the term's % off the repair parts and labor. The rail shows the same.
  const diagOf = (scope: string) => {
    const rows = (fees ?? []).filter((f) => f.active && f.segment === "residential" && f.section === "residential_diag" && f.plan_scope === scope && (f.zone ?? "any") === "any" && !f.after_hours);
    return rows.length ? Math.min(...rows.map((f) => Number(f.amount) || 0)) : 0;
  };
  const savings = (() => {
    if (!isService || !plan.on || !focus) return { diag: 0, repair: 0, pct: 0 };
    const std = diagOf("standard"), mem = diagOf("plus");
    const diagLines = focus.lines.filter((l) => /diagnos/i.test(l.description ?? "") && std > 0 && Math.abs(Number(l.unit_price) - std) < 0.005);
    const diag = std > mem ? diagLines.length * (std - mem) : 0;
    const pct = tiers && tiers.length ? repairPctForTerm(tiers, plan.term) : 0;
    const eligible = focus.lines.filter((l) => !diagLines.includes(l) && l.role !== "recommended" && memberDiscountByRule({ kind: l.kind || "custom", description: l.description, item_id: l.item_id ?? null }, null));
    const repair = eligible.reduce((t, l) => t + lineTotal(l), 0) * pct;
    return { diag, repair, pct };
  })();
  return (
    <aside className="space-y-3 lg:sticky lg:top-4">
      <Card className="p-4">
        <div className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-400">What they will see</div>
        <div className="mt-2.5 space-y-1.5 text-[13px]">
          {sorted.length === 0 && <div className="text-slate-400">—</div>}
          {sorted.map((o) => (
            <div key={o.id} className={cn("flex items-baseline justify-between gap-3", focus?.id === o.id ? "font-bold text-slate-900" : "text-slate-600")}>
              <span className="truncate">{isService && sorted.length === 1 ? "Repair" : (o.label || o.tier)}{quote.recommended_option_id === o.id ? " · ★" : ""}</span>
              <span className="tabular-nums">{o.lines.length ? money(optionTotal(o)) : "—"}</span>
            </div>
          ))}
          {plan.on && (
            <div className="flex items-baseline justify-between gap-3 text-emerald-700">
              <span className="truncate">Maintenance Plus · {plan.term} yr{plan.systems > 1 ? ` · ${plan.systems} systems` : ""}</span>
              <span className="tabular-nums font-semibold">{planLine === 0 ? "included" : planLine != null ? `+${money(planLine)}` : "—"}</span>
            </div>
          )}
          {savings.diag > 0 && (
            <div className="flex items-baseline justify-between gap-3 text-rose-600"><span>Member diagnostic</span><span className="tabular-nums font-semibold">−{money(savings.diag)}</span></div>
          )}
          {savings.repair > 0 && (
            <div className="flex items-baseline justify-between gap-3 text-rose-600"><span>{Math.round(savings.pct * 100)}% off the repair</span><span className="tabular-nums font-semibold">−{money(savings.repair)}</span></div>
          )}
          {focus && focus.lines.length > 0 && (
            <div className="flex items-baseline justify-between gap-3 border-t border-slate-100 pt-2">
              <b className="text-slate-900">Due to Sedgwick</b>
              <b className="text-lg tabular-nums text-slate-900 [font-family:var(--font-display)]">{money(Math.max(0, optionTotal(focus) + (planLine ?? 0) - savings.diag - savings.repair))}</b>
            </div>
          )}
        </div>
      </Card>
      <Card className="p-4">
        <div className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-400">This customer</div>
        <div className="mt-2.5 space-y-1.5 text-[13px]">
          <div className="flex items-baseline justify-between gap-3"><span className="text-slate-500">Plan</span>
            <b className={cn("truncate", st?.state === "active" ? "text-emerald-700" : st?.state === "expiring" ? "text-amber-700" : "text-slate-800")}>
              {!st || st.state === "none" ? "Not a member" : st.state === "active" ? `Member through ${monLabel(st.horizon)}` : st.state === "expiring" ? `Expires ${monLabel(st.horizon)}` : `Lapsed ${monLabel(st.horizon)}`}
            </b></div>
          {d && (d.system_age_years ?? 0) > 0 && <div className="flex items-baseline justify-between gap-3"><span className="text-slate-500">System age</span><b className="tabular-nums">{d.system_age_years} yrs</b></div>}
          {d && Number(d.recent_spend ?? 0) > 0 && <div className="flex items-baseline justify-between gap-3"><span className="text-slate-500">Repairs · {d.recent_years} yrs</span><b className="tabular-nums">{money(Number(d.recent_spend))} · {d.recent_visits} visit{d.recent_visits === 1 ? "" : "s"}</b></div>}
          {d && Number(d.lifetime_spend ?? 0) > Number(d.recent_spend ?? 0) && <div className="flex items-baseline justify-between gap-3"><span className="text-slate-500">Lifetime repairs</span><b className="tabular-nums">{money(Number(d.lifetime_spend))}</b></div>}
        </div>
      </Card>
    </aside>
  );
}

/* ── PROPOSAL-VISION-02: the packages shelf. Chips of every active replace
   package (Settings → Quotes → Packages); a tap fills the FIRST EMPTY
   collection — equipment, included accessories with their $0 marks, the
   blurb, the brand. Hidden when no packages exist; chips disable once every
   collection has content (clear a collection to refill). ─────────────── */
function PackageShelf({ options, locked, sizing }: { options: QuoteOptionFull[]; locked: boolean; sizing: SizingSuggestion | null }) {
  const { data: pkgs } = useQuotePackages("replace");
  const apply = useApplyQuotePackage();
  const [armId, setArmId] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const list = pkgs ?? [];
  if (list.length === 0) return null;

  const sorted = [...options].sort((a, b) => a.sort - b.sort);
  // Which collection a package aims at: its own tier (Settings → Quotes →
  // Packages), read off the label when the tier isn't set. A tier match that's
  // EMPTY wins; then any empty collection; a full tier-match means REPLACE
  // (two-tap confirm) — so a chip is never dead, it just asks first.
  const tierOf = (p: QuotePackage): QuoteTier | null =>
    (p.tier as QuoteTier | null) ??
    (/\bbest\b/i.test(p.label) ? "best" : /\bbetter\b/i.test(p.label) ? "better" : /\bgood\b/i.test(p.label) ? "good" : null);
  const targetFor = (p: QuotePackage): { opt: QuoteOptionFull; replace: boolean } | null => {
    const t = tierOf(p);
    const tierEmpty = t ? sorted.find((o) => o.tier === t && o.lines.length === 0) : null;
    const anyEmpty = sorted.find((o) => o.lines.length === 0) ?? null;
    const tierFull = t ? (sorted.find((o) => o.tier === t) ?? null) : null;
    const opt = tierEmpty ?? anyEmpty ?? tierFull ?? sorted[0] ?? null;
    return opt ? { opt, replace: opt.lines.length > 0 } : null;
  };
  const tap = (p: QuotePackage) => {
    const tgt = targetFor(p);
    if (!tgt) return;
    if (tgt.replace && armId !== p.id) {
      setArmId(p.id);
      return;
    }
    setArmId(null);
    setNote(null);
    apply.mutate(
      { option: tgt.opt, pkg: p, replace: tgt.replace, sizing },
      {
        onSuccess: (res) => {
          const where = tgt.opt.label || tgt.opt.tier;
          setNote(
            res.swapped.length
              ? `${p.label} → ${where} · sized to the home: ${res.swapped.join(" · ")}`
              : `${p.label} → ${where}`,
          );
        },
        onError: (e) => setNote(`Couldn't apply — ${(e as Error).message || "try again"}`),
      },
    );
  };

  // BUILDER-FLOW-01: one quiet link in the Packages header; the chips open under it.
  return (
    <div className="relative">
      <button type="button" disabled={locked} onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:text-brand-900 disabled:opacity-50">
        <Boxes className="h-3.5 w-3.5" /> Packages <ChevronDown className="h-3 w-3 opacity-70" />
      </button>
      {open && <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />}
      {open && (
      <div className="absolute right-0 z-30 mt-2 w-[26rem] max-w-[90vw] rounded-2xl border border-hairline bg-white p-3.5 shadow-e3">
      <div className="flex flex-wrap items-center gap-2">
        {list.map((p) => {
          const tgt = targetFor(p);
          const armed = armId === p.id;
          return (
            <button
              key={p.id}
              type="button"
              disabled={locked || apply.isPending || !tgt}
              onClick={() => tap(p)}
              title={
                !tgt
                  ? "No collection to fill"
                  : tgt.replace
                    ? `${tgt.opt.label || tgt.opt.tier} already has content — tap twice to replace it with: ${p.lines.map((l) => l.description).filter(Boolean).slice(0, 3).join(" · ")}`
                    : `Fills the ${tgt.opt.label || tgt.opt.tier} collection: ${p.lines.map((l) => l.description).filter(Boolean).slice(0, 3).join(" · ")}`
              }
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45",
                armed
                  ? "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"
                  : "border-hairline bg-white text-slate-700 hover:border-brand-300 hover:text-brand-700",
              )}
            >
              {armed ? `Replace ${tgt?.opt.label || tgt?.opt.tier}?` : p.label}
              {!armed && <span className="text-[11px] font-semibold text-slate-400">{money(pkgTotal(p))}</span>}
            </button>
          );
        })}
      </div>
      {(note || sizing) && (
        <div className="mt-1.5 text-[11px] text-slate-400">
          {note ? (
            <span className="font-medium text-emerald-700">{note}</span>
          ) : sizing ? (
            <>Sized to this home{sizing.coolingTons ? ` · ~${sizing.coolingTons} ton A/C` : ""}{sizing.heatInputKbtu ? ` · ${sizing.heatInputKbtu}K BTU furnace` : ""}</>
          ) : null}
        </div>
      )}
      </div>
      )}
    </div>
  );
}

/* ── QUOTE-POLISH-11: the two notes (his app's "internal / external notes").
   quote.notes is OFFICE-ONLY and structured — the wizard writes a provenance
   line ("Built on site at call #N by Miles.") plus "Note to the office: …";
   the office edits only that tail so the stamp survives and the phone's
   resume-parse keeps working (split/join live in proposalSend.ts now — the
   Send Link sheet uses the same convention). quote.homeowner_note is what the
   HOMEOWNER reads on their proposal page. Saved on blur. ──────────────── */
function NotesRow({ quote }: { quote: Quote }) {
  const update = useUpdateQuote();
  const split = useMemo(() => splitOfficeNote(quote.notes), [quote.notes]);
  const [office, setOffice] = useState(split.note);
  const [home, setHome] = useState(quote.homeowner_note ?? "");
  const [saved, setSaved] = useState<"office" | "home" | null>(null);
  // Follow outside changes (the phone wizard updating the same quote) unless
  // the box is mid-edit.
  useEffect(() => { setOffice((cur) => (cur === split.note ? cur : document.activeElement?.getAttribute("data-note") === "office" ? cur : split.note)); }, [split.note]);
  useEffect(() => { const v = quote.homeowner_note ?? ""; setHome((cur) => (cur === v ? cur : document.activeElement?.getAttribute("data-note") === "home" ? cur : v)); }, [quote.homeowner_note]);
  const flash = (which: "office" | "home") => { setSaved(which); window.setTimeout(() => setSaved((s) => (s === which ? null : s)), 1800); };
  const commitOffice = () => {
    if (office.trim() === split.note) return;
    update.mutate({ id: quote.id, notes: joinOfficeNote(split.head, office) }, { onSuccess: () => flash("office") });
  };
  const commitHome = () => {
    if (home.trim() === (quote.homeowner_note ?? "").trim()) return;
    update.mutate({ id: quote.id, homeowner_note: home.trim() || null }, { onSuccess: () => flash("home") });
  };
  const box = "mt-1.5 w-full resize-y rounded-xl border px-3 py-2 text-[13px] leading-relaxed text-slate-800 placeholder:text-slate-400 focus:outline-none";
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <label className="block">
        <span className="flex items-center justify-between gap-2">
          <span className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-500">Note to the homeowner</span>
          {saved === "home" && <span className="text-[11px] font-medium text-emerald-700">Saved ✓</span>}
        </span>
        <textarea
          data-note="home"
          value={home}
          onChange={(e) => setHome(e.target.value)}
          onBlur={commitHome}
          rows={2}
          placeholder="Optional"
          className={cn(box, "border-hairline bg-white focus:border-emerald-500")}
        />
      </label>
      <label className="block">
        <span className="flex items-center justify-between gap-2">
          <span className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-500">Note to the office</span>
          {saved === "office" && <span className="text-[11px] font-medium text-emerald-700">Saved ✓</span>}
        </span>
        <textarea
          data-note="office"
          value={office}
          onChange={(e) => setOffice(e.target.value)}
          onBlur={commitOffice}
          rows={2}
          placeholder="Optional"
          className={cn(box, "border-hairline bg-slate-50/60 focus:border-brand-400 focus:bg-white")}
        />
        {split.head && <span className="mt-1 block text-[11px] text-slate-400">{split.head}</span>}
      </label>
    </div>
  );
}

/** BUILDER-FLOW-01: the package layout — Good / Better / Best or one package, and which
 *  equipment slots the packages carry — behind one quiet "Layout ▾" in the Packages header. */
function LayoutMenu({
  quote, options, allCats, shown,
}: { quote: Quote; options: QuoteOptionFull[]; allCats: QuoteCategory[]; shown: QuoteCategory[] }) {
  const update = useUpdateQuote();
  const addOption = useAddQuoteOption();
  const removeOption = useRemoveQuoteOption();
  const [open, setOpen] = useState(false);
  const [addingCat, setAddingCat] = useState(false);
  const qt = quote.quote_type || "install";
  const single = (quote.offering_mode || "tiered") === "single";
  const shownKeys = shown.map((c) => c.key);
  const available = allCats
    .filter((c) => c.is_active && !c.parent_key && !shownKeys.includes(c.key) && (c.applies_to === "both" || c.applies_to === qt))
    .sort((a, b) => a.sort - b.sort);
  const setShape = (mode: "tiered" | "single") => {
    if (mode === "single" && options.length > 1) {
      const [, ...rest] = [...options].sort((a, b) => a.sort - b.sort);
      rest.forEach((o) => removeOption.mutate(o.id));
    } else if (mode === "tiered" && options.length < 3) {
      const labels = ["Best Collection", "Better Collection", "Good Collection"];
      const tiers = ["best", "better", "good"] as const;
      for (let i = options.length; i < 3; i++) {
        addOption.mutate({ quote_id: quote.id, tier: tiers[i], label: labels[i], brand: options[0]?.brand ?? "Lennox", sort: i });
      }
    }
    update.mutate({ id: quote.id, offering_mode: mode });
  };
  const addCategory = (key: string) => { update.mutate({ id: quote.id, category_keys: [...shownKeys, key] }); setAddingCat(false); };
  const removeCategory = (key: string) => update.mutate({ id: quote.id, category_keys: shownKeys.filter((k) => k !== key) });
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800">
        <Settings2 className="h-3.5 w-3.5" /> Layout <ChevronDown className="h-3 w-3 opacity-70" />
      </button>
      {open && <div className="fixed inset-0 z-20" onClick={() => { setOpen(false); setAddingCat(false); }} />}
      {open && (
        <div className="absolute right-0 z-30 mt-2 w-80 rounded-2xl border border-hairline bg-white p-4 shadow-e3">
          <Segmented label="Packages" value={single ? "single" : "tiered"} onChange={(v) => setShape(v as "tiered" | "single")} options={[{ v: "tiered", l: "Good / Better / Best" }, { v: "single", l: "One" }]} />
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Slots</span>
            {shown.map((c) => (
              <span key={c.key} className="group inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-hairline bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                {c.label}
                <button onClick={() => removeCategory(c.key)} title={`Remove ${c.label} from this quote`} className="text-slate-300 hover:text-rose-500">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            {available.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setAddingCat((v) => !v)}
                  className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700 hover:bg-brand-100"
                >
                  <Plus className="h-3 w-3" /> Add slot
                </button>
                {addingCat && (
                  <div className="absolute left-0 top-7 z-40 w-44 rounded-lg border border-hairline bg-white py-1 shadow-e3">
                    {available.map((c) => (
                      <button key={c.key} onClick={() => addCategory(c.key)} className="block w-full px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50">
                        {c.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DiagnosticAdder({ options }: { options: QuoteOptionFull[] }) {
  const fees = useDiagnosticFees();
  const addLine = useAddQuoteLine();
  const add = (feeId: string) => {
    const fee = (fees.data ?? []).find((f) => f.id === feeId);
    if (!fee) return;
    options.forEach((o) => addLine.mutate({ option_id: o.id, kind: "custom", description: fee.label, unit_price: fee.amount }));
  };
  return (
    <select
      value=""
      onChange={(e) => { if (e.target.value) add(e.target.value); }}
      aria-label="Add a diagnostic fee to every option"
      className="w-auto max-w-[8.5rem] appearance-none rounded-md border border-hairline bg-white px-2 py-0.5 text-xs font-semibold text-slate-600 focus:border-brand-400 focus:outline-none"
    >
      <option value="">Diagnostic ▾</option>
      {(fees.data ?? []).map((f) => (
        <option key={f.id} value={f.id}>{f.label} — ${f.amount}</option>
      ))}
    </select>
  );
}

/** A tiny segmented control (Type / Shape pickers in the settings bar). */
function Segmented({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { v: string; l: string }[] }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <div className="inline-flex rounded-full bg-slate-200/70 p-0.5">
        {options.map((o) => (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            className={cn(
              "rounded-full px-3 py-1 text-xs font-semibold transition-colors",
              value === o.v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800",
            )}
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Header cluster: status · Favorites ▾ · Review & Present · ···
 *  PROPOSAL-FLOW-02 (Seth 2026-10-16: "do we want techs or office to put
 *  together a quote and just send it to the customer without reviewing how it
 *  will look?"): there is no Send from here any more. Review & Present opens
 *  the customer view — the same page the homeowner gets — with the staff bar
 *  docked: Send Link (text / email / copy, with the notes), Accepted in Person,
 *  Not Now. Opening it never changes the status; sending does. */
function QuoteActions({
  quote,
  locked,
  hasContent,
  practiceOn,
  onTogglePractice,
  parts = "all",
}: {
  quote: Quote;
  locked?: boolean;
  /** QUOTE-POLISH-11: loading a Favorite over a built proposal asks twice. */
  hasContent?: boolean;
  practiceOn?: boolean;
  onTogglePractice?: () => void;
  /** BUILDER-FLOW-01: the cluster split across the page — the ⋯ in the header, Favorites in the
   *  Packages header, the status · clock · Review & Present under Present or send. */
  parts?: "all" | "menu" | "favorites" | "send";
}) {
  const navigate = useNavigate();
  const setStatus = useSetQuoteStatus();
  const clone = useCloneQuote();
  const delQuote = useDeleteQuote();
  const extend = useExtendQuote();
  // PERM-ENFORCE-01: deleting a proposal is its own power (quotes.delete).
  const canDelete = useCan("quotes.delete");
  // UI-WAVE-02: the ··· menu (same idiom as the 360 header).
  const [moreOpen, setMoreOpen] = useState(false);
  // QUOTE-FAV-01 → QUOTE-POLISH-11: one Favorites ▾ menu — load one (fills all
  // three collections) or save this build. QUOTE-FAV-02: favorites are
  // PERSONAL (each user's own list); rename / replace / delete live in the menu.
  const saveTpl = useSaveAsTemplate();
  const replaceTpl = useReplaceTemplate();
  const [favOpen, setFavOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const exp = quoteExpiry(quote);
  const expDate = exp.expiresAt?.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const backTarget = useBackTarget({ url: "/quotes", label: "Proposals" });
  const doDelete = () => {
    const msg = quote.job_id
      ? "Delete this proposal? The install job created from it STAYS — only the proposal goes away. This can't be undone."
      : "Delete this proposal? This can't be undone.";
    // 360-PROPOSALS-02 (Seth 2026-10-30: deleting from the 360 "took me to the proposals screen"):
    // back to the screen the builder was opened from; the Proposals list only when there is none.
    if (confirm(msg)) delQuote.mutate(quote.id, { onSuccess: () => navigate(backTarget.url) });
  };
  const [flashMsg, setFlashMsg] = useState<string | null>(null);
  // One quiet feedback line under the cluster — menu actions report here
  // (the menu closes on click, so in-button feedback would go unseen).
  const flash = (msg: string) => {
    setFlashMsg(msg);
    window.setTimeout(() => setFlashMsg((m) => (m === msg ? null : m)), 2500);
  };
  const proposalUrl = quote.token ? proposalUrlFor(quote.token) : null;
  const copy = async () => {
    if (!proposalUrl) return;
    if (await copyText(proposalUrl)) flash("Proposal link copied — this alone doesn't mark it Sent");
  };
  const reBid = () => clone.mutate(quote.id, { onSuccess: (id) => navigate(`/quotes/${id}`) });
  // PROPOSAL-FLOW-02: Review & Present opens the customer view in its own tab —
  // nothing is marked, nothing is sent. The staff bar on that page is where
  // the link goes out and the decision gets recorded.
  const review = () => {
    if (!proposalUrl) return;
    window.open(proposalUrl, "_blank");
  };
  const sentHint = quote.status === "sent" || quote.status === "viewed"
    ? `Sent${quote.sent_at ? ` ${new Date(quote.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}`
    : null;
  const showMenu = parts === "all" || parts === "menu";
  const showFav = (parts === "all" || parts === "favorites") && !locked;
  const showSend = parts === "all" || parts === "send";
  return (
    <div className={cn("flex gap-1.5", parts === "send" ? "w-full flex-col items-start" : "flex-col items-end")}>
      {/* QUOTE-LIFECYCLE-01: the clock, visible — and one click to give more time. */}
      {showSend && exp.expiresAt && (
        <div className="flex items-center gap-2">
          <span className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
            exp.expired ? "bg-amber-100 text-amber-800"
              : (exp.daysLeft ?? 99) <= 7 ? "bg-amber-50 text-amber-700"
              : "bg-slate-100 text-slate-500",
          )}>
            <Clock className="h-3.5 w-3.5" />
            {exp.expired ? `Expired ${expDate}` : `Expires ${expDate}${exp.daysLeft != null ? ` · ${exp.daysLeft}d left` : ""}`}
          </span>
          <button
            type="button"
            disabled={extend.isPending}
            title="Reset the clock from today (org setting: proposal valid days) — an expired proposal goes live again at the same price"
            onClick={() => extend.mutate({ id: quote.id, currentStatus: quote.status })}
            className="rounded-full border border-hairline bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 transition-colors hover:border-brand-400 hover:text-brand-700 disabled:opacity-50"
          >
            {extend.isPending ? "Extending…" : "Extend"}
          </button>
        </div>
      )}
      {/* UI-WAVE-02: status field · one quiet · one primary · ··· */}
      <div className={cn("flex flex-wrap items-center gap-2", parts === "send" ? "justify-start" : "justify-end")}>
        {showSend && (
        <label className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-hairline bg-white px-2.5 text-xs shadow-e1">
          <span className="font-semibold uppercase tracking-wide text-slate-400">Status</span>
          <select
            value={quote.status}
            onChange={(e) => setStatus.mutate({ id: quote.id, status: e.target.value as (typeof QUOTE_STATUSES)[number] })}
            className="cursor-pointer appearance-none bg-transparent pr-1 font-semibold text-slate-700 focus:outline-none"
          >
            {QUOTE_STATUSES.filter((s) => s !== "viewed").map((s) => (
              <option key={s} value={s}>{QUOTE_STATUS_LABEL[s]}</option>
            ))}
          </select>
        </label>
        )}
        {/* QUOTE-POLISH-11: Favorites ▾ — load or save, one place (the mock). */}
        {showFav && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setFavOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={favOpen}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-hairline bg-white px-3 text-xs font-semibold text-slate-700 shadow-e1 transition-colors hover:bg-amber-50 hover:text-amber-800"
            >
              <Star className="h-3.5 w-3.5 text-amber-500" /> Favorites <ChevronDown className="h-3.5 w-3.5 opacity-70" />
            </button>
            {favOpen && (
              <FavoritesMenu
                quote={quote}
                hasContent={!!hasContent}
                onClose={() => setFavOpen(false)}
                onSave={() => { setFavOpen(false); setSaveOpen(true); }}
                onLoaded={(name) => { setFavOpen(false); flash(`Filled from “${name}” ✓`); }}
              />
            )}
          </div>
        )}
        {saveOpen && (
          <SaveFavoriteDialog
            quote={quote}
            busy={saveTpl.isPending || replaceTpl.isPending}
            onClose={() => setSaveOpen(false)}
            onSave={(name, shared, byName) => saveTpl.mutate({ quote, name, shared, byName }, { onSuccess: () => { setSaveOpen(false); flash(shared ? "Saved and shared with the team ✓" : "Saved as a Favorite ✓"); } })}
            onReplace={(t) => replaceTpl.mutate({ template: t, quote }, { onSuccess: () => { setSaveOpen(false); flash(`Updated “${t.template_name ?? t.title ?? "Favorite"}” ✓`); } })}
          />
        )}
        {showSend && proposalUrl && (
          <button
            type="button"
            onClick={review}
            title={sentHint
              ? `${sentHint} — open the customer view to present it again, send the link again, or record the decision`
              : "Open the customer view — exactly what the homeowner sees. Send the link, take an acceptance or a Not Now from the bar at the bottom. Opening it doesn't mark anything."}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-brand-600 px-3.5 text-xs font-bold text-white shadow-e1 transition-colors hover:bg-brand-700"
          >
            <Presentation className="h-3.5 w-3.5" /> Review &amp; Present
          </button>
        )}
        {showMenu && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setMoreOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            aria-label="More actions"
            title="More actions"
            className="grid h-9 w-9 place-items-center rounded-lg border border-hairline bg-white text-slate-500 shadow-e1 transition-colors hover:bg-slate-50 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
          >
            <MoreHorizontal className="h-[18px] w-[18px]" />
          </button>
          {moreOpen && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setMoreOpen(false)} />
              <div role="menu" data-glass="panel" className="absolute right-0 z-30 mt-1 w-64 overflow-hidden rounded-xl py-1">
                <button type="button" role="menuitem" disabled={clone.isPending}
                  onClick={() => { setMoreOpen(false); reBid(); }}
                  className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50">
                  <CopyPlus className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" />
                  <span>
                    <span className="block text-xs font-medium text-slate-800">{clone.isPending ? "Re-bidding…" : "Re-bid"}</span>
                    <span className="block text-[11px] leading-snug text-slate-400">Clone into a fresh draft to update pricing and re-send</span>
                  </span>
                </button>
                {proposalUrl && (
                  <button type="button" role="menuitem"
                    onClick={() => { setMoreOpen(false); void copy(); }}
                    className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50">
                    <Copy className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" />
                    <span>
                      <span className="block text-xs font-medium text-slate-800">Copy Link</span>
                      <span className="block text-[11px] leading-snug text-slate-400">The customer-facing page — to send it, use Review &amp; Present → Send Link</span>
                    </span>
                  </button>
                )}
                {onTogglePractice && (
                  <button type="button" role="menuitem"
                    onClick={() => { setMoreOpen(false); onTogglePractice(); }}
                    className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50">
                    <FlaskConical className={cn("mt-0.5 h-4 w-4 shrink-0", practiceOn ? "text-violet-600" : "text-slate-400")} />
                    <span>
                      <span className="block text-xs font-medium text-slate-800">{practiceOn ? "Turn Practice Off" : "Practice Mode"}</span>
                      <span className="block text-[11px] leading-snug text-slate-400">
                        {practiceOn ? "Make this build real — it shows on Proposals and the 360" : "Sandbox build — invisible to the office until you flip it back"}
                      </span>
                    </span>
                  </button>
                )}
                {canDelete && <div className="my-1 border-t border-hairline/70" />}
                {canDelete && <button type="button" role="menuitem" disabled={delQuote.isPending}
                  onClick={() => { setMoreOpen(false); doDelete(); }}
                  className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-rose-50 focus-visible:outline-none focus-visible:bg-rose-50">
                  <Trash2 className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
                  <span>
                    <span className="block text-xs font-medium text-rose-700">{delQuote.isPending ? "Deleting…" : "Delete Proposal"}</span>
                    <span className="block text-[11px] leading-snug text-slate-400">{quote.job_id ? "The install job it created stays" : "Removes this proposal for good"}</span>
                  </span>
                </button>}
              </div>
            </>
          )}
        </div>
        )}
      </div>
      {flashMsg && <div className="text-[11px] font-medium text-slate-500">{flashMsg}</div>}
    </div>
  );
}

// ---- Proposal activity (the lifecycle "audit report", inline on the quote) ----

const EVENT_STYLE: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string; ring: string }> = {
  proposal_created: { icon: FileEdit, color: "text-slate-500", ring: "bg-slate-100" },
  // PROPOSAL-FLOW-02: staff opened the customer view (presented / reviewed) — not a send.
  proposal_presented: { icon: Presentation, color: "text-indigo-600", ring: "bg-indigo-50" },
  proposal_sent: { icon: Send, color: "text-blue-600", ring: "bg-blue-50" },
  proposal_viewed: { icon: Eye, color: "text-violet-600", ring: "bg-violet-50" },
  proposal_accepted: { icon: CheckCircle2, color: "text-emerald-600", ring: "bg-emerald-50" },
  proposal_declined: { icon: XCircle, color: "text-rose-600", ring: "bg-rose-50" },
  message: { icon: MessageSquare, color: "text-amber-600", ring: "bg-amber-50" },
};
const eventStyle = (kind: string) =>
  EVENT_STYLE[kind] ?? { icon: Clock, color: "text-slate-500", ring: "bg-slate-100" };
const dtUS = (s: string) =>
  new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
const actorLabel = (a: string | null) =>
  a === "customer" ? "Homeowner" : a === "office" ? "Office" : a || "";

function QuoteActivity({ quoteId, custno, views }: { quoteId: string; custno: string | null; views?: { count: number; last: string | null } }) {
  const { data, isLoading } = useQuoteEvents(quoteId);
  const events = data ?? [];
  // QUOTE-ASK-01: the homeowner's questions, answered from right here.
  const { data: msgData } = useQuoteMessages(quoteId);
  const msgs = (msgData ?? []).filter((m) => m.direction === "in");
  const openCount = msgs.filter((m) => !m.replied_at).length;
  const markReplied = useMarkMessageReplied();
  // QUOTE-POLISH-05: a reply sent by text/email is also written to the thread
  // (direction out) so the homeowner page shows the conversation — and the
  // question card here shows what was said back.
  const recordReply = useRecordQuoteReply();
  const outs = (msgData ?? []).filter((m) => m.direction === "out");
  const repliesFor = (m: { id: string; created_at: string }) => {
    const idx = msgs.findIndex((x) => x.id === m.id); // msgs are newest-first
    const newer = idx > 0 ? msgs[idx - 1].created_at : null;
    return outs
      .filter((o) => o.created_at > m.created_at && (!newer || o.created_at < newer))
      .sort((a, b) => a.created_at.localeCompare(b.created_at));
  };
  const [reply, setReply] = useState<{ id: string; via: "text" | "email"; to: string | null } | null>(null);
  const looksPhone = (c: string | null) => !!c && /\d{7,}/.test(c.replace(/\D/g, ""));
  const looksEmail = (c: string | null) => !!c && /@/.test(c);
  const dtShort = (iso: string) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  // events come newest-first; the EARLIEST of a kind is the last in the array.
  const firstAt = (kind: string) => {
    const hits = events.filter((e) => e.kind === kind);
    return hits.length ? hits[hits.length - 1].created_at : null;
  };
  // QUOTE-POLISH-05: real homeowner opens (staff opens never count) — the
  // counter lives on the quote; the first open is still the Viewed milestone.
  const viewCount = views?.count ?? events.filter((e) => e.kind === "proposal_viewed").length;
  const lastViewed = views?.last ?? null;
  const milestones = [
    { kind: "proposal_created", label: "Created" },
    { kind: "proposal_presented", label: "Presented" },
    { kind: "proposal_sent", label: "Sent" },
    { kind: "proposal_viewed", label: "Viewed" },
    { kind: "proposal_accepted", label: "Accepted" },
  ];

  return (
    <section className="mt-6 rounded-2xl border border-hairline bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Activity className="h-4 w-4 text-brand-600" />
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Proposal Activity</h2>
        {viewCount > 0 && (
          <span className="ml-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700" title={lastViewed ? `Last opened ${dtUS(lastViewed)}` : undefined}>
            Opened {viewCount}×{lastViewed ? ` · last ${dtShort(lastViewed)}` : ""}
          </span>
        )}
        {openCount > 0 && (
          <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800">
            <MessageSquare className="h-3 w-3" /> {openCount} question{openCount === 1 ? "" : "s"} waiting
          </span>
        )}
      </div>

      {/* QUOTE-ASK-01: homeowner questions — answer by text or email, then mark
          replied so the lanes and the Today desk stop waving. */}
      {msgs.length > 0 && (
        <div className="mb-5 space-y-2">
          {msgs.map((m) => {
            const open = !m.replied_at;
            return (
              <div key={m.id} className={cn("rounded-xl border p-3.5", open ? "border-amber-200 bg-amber-50/60" : "border-hairline bg-slate-50/50")}>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className={cn("inline-flex items-center gap-1 font-bold", open ? "text-amber-800" : "text-slate-500")}>
                    <MessageSquare className="h-3.5 w-3.5" /> Homeowner asked
                  </span>
                  <span className="text-slate-400">{dtShort(m.created_at)}</span>
                  {m.contact && <span className="rounded-full bg-white px-2 py-0.5 font-medium text-slate-600 ring-1 ring-slate-200">Reach them: {m.contact}</span>}
                  {!open && <span className="ml-auto text-slate-400">Replied {m.replied_by ? `by ${m.replied_by.split("@")[0]} ` : ""}{m.replied_at ? dtShort(m.replied_at) : ""}</span>}
                </div>
                <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-slate-800">{m.body}</p>
                {repliesFor(m).map((r) => (
                  <div key={r.id} className="mt-2 rounded-lg bg-white/80 px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200">
                    <span className="font-bold text-slate-700">{r.actor ? r.actor.split("@")[0] : "Office"} replied</span>
                    <span className="text-slate-400"> · {dtShort(r.created_at)}{r.contact ? ` · to ${r.contact}` : ""}</span>
                    <p className="mt-0.5 whitespace-pre-line text-[12.5px] leading-relaxed text-slate-700">{r.body}</p>
                  </div>
                ))}
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                  {custno && (
                    <button type="button" onClick={() => setReply({ id: m.id, via: "text", to: looksPhone(m.contact) ? m.contact : null })}
                      className="inline-flex items-center gap-1.5 rounded-full bg-brand-600 px-3 py-1 text-xs font-bold text-white transition-colors hover:bg-brand-700">
                      <Reply className="h-3.5 w-3.5" /> Reply by Text
                    </button>
                  )}
                  {custno && (
                    <button type="button" onClick={() => setReply({ id: m.id, via: "email", to: looksEmail(m.contact) ? m.contact : null })}
                      className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition-colors hover:border-brand-300 hover:text-brand-700">
                      <Mail className="h-3.5 w-3.5" /> Reply by Email
                    </button>
                  )}
                  <button type="button" disabled={markReplied.isPending}
                    onClick={() => markReplied.mutate({ id: m.id, quoteId, replied: open })}
                    className={cn("ml-auto rounded-full px-3 py-1 text-xs font-semibold transition-colors", open ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "text-slate-400 hover:text-slate-600")}>
                    {open ? "Mark Replied ✓" : "Reopen"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {reply && custno && reply.via === "text" && (
        <ComposeTextModal custno={custno} initialTo={reply.to ?? undefined} initialBody="Hi — thanks for the question about your proposal. "
          onSent={(sent) => { if (sent?.body) recordReply.mutate({ quoteId, body: sent.body, to: sent.to ?? null }); markReplied.mutate({ id: reply.id, quoteId, replied: true }); }} onClose={() => setReply(null)} />
      )}
      {reply && custno && reply.via === "email" && (
        <ComposeEmailModal custno={custno} initialTo={reply.to ?? undefined} initialSubject="About your Sedgwick Heating proposal" initialBody="Hi — thanks for the question about your proposal. "
          onSent={(sent) => { if (sent?.body) recordReply.mutate({ quoteId, body: sent.body, to: sent.to ?? null }); markReplied.mutate({ id: reply.id, quoteId, replied: true }); }} onClose={() => setReply(null)} />
      )}

      {/* Milestone strip — at-a-glance lifecycle */}
      <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-5">
        {milestones.map((m) => {
          const at = firstAt(m.kind);
          const s = eventStyle(m.kind);
          const Icon = s.icon;
          return (
            <div
              key={m.kind}
              className={cn("rounded-xl border p-3", at ? "border-hairline bg-white" : "border-dashed border-hairline bg-slate-50/50")}
            >
              <div className="flex items-center gap-1.5">
                <Icon className={cn("h-3.5 w-3.5", at ? s.color : "text-slate-300")} />
                <span className={cn("text-xs font-semibold", at ? "text-slate-700" : "text-slate-400")}>{m.label}</span>
              </div>
              <div className={cn("mt-1 text-[11px]", at ? "text-slate-500" : "text-slate-300")}>{at ? dtUS(at) : "—"}</div>
            </div>
          );
        })}
      </div>

      {/* Detailed timeline */}
      {isLoading ? (
        <div className="text-xs text-slate-400">Loading activity…</div>
      ) : events.length === 0 ? (
        <div className="text-xs text-slate-400">No activity yet.</div>
      ) : (
        <ol className="space-y-3">
          {events.map((e) => {
            const s = eventStyle(e.kind);
            const Icon = s.icon;
            return (
              <li key={e.id} className="flex items-start gap-3">
                <span className={cn("mt-0.5 grid h-7 w-7 flex-shrink-0 place-items-center rounded-full", s.ring)}>
                  <Icon className={cn("h-4 w-4", s.color)} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-700">{e.summary || e.kind}</div>
                  <div className="text-[11px] text-slate-400">
                    {dtUS(e.created_at)}{e.actor ? ` · ${actorLabel(e.actor)}` : ""}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

/**
 * Repair vs. Replace — the customer's OWN past service spend + the system's age,
 * contrasted with the Replace option's price so the case for a new system makes
 * itself. Facts forward, with a soft + honest recommendation. Service quotes
 * only; hidden when there's no history worth showing (never an empty panel).
 */
function CollectionCard({ o, quote, cats, allCats, siblings, recommended, onToggleRecommend, onAdd }: { o: QuoteOptionFull; quote: Quote; cats: QuoteCategory[]; allCats: QuoteCategory[]; siblings: QuoteOptionFull[]; recommended: boolean; onToggleRecommend: () => void; onAdd: (cat: QuoteCategory, sub?: SubPick | null) => void }) {
  const setBrand = useSetOptionBrand();
  const brands = useQuoteBrands();
  const remove = useRemoveQuoteLine();
  const updateOption = useUpdateQuoteOption();
  const plans = useFinancingPlans();
  const utils = useUtilities();
  const copyExtras = useCopyOptionExtras();
  const subtotal = optionSubtotal(o);
  const total = optionTotal(o);
  const plan = (plans.data ?? []).find((p) => p.id === o.financing_plan_id) ?? null;
  const monthly = plan ? estMonthly(total, plan) : 0;
  // QUOTE-POLISH-01 "fill it out once": mirror this collection's money story
  // (rebate + discounts + financing) and/or its accessories to the others.
  const others = siblings.filter((s) => s.id !== o.id);
  const equipKeyList = allCats.filter((c) => c.kind === "equipment").map((c) => c.key);
  const equipKeySet = new Set(equipKeyList);
  const hasMoney = !!o.rebate_utility_id || (o.rebate_total ?? 0) > 0 || (o.discounts ?? []).length > 0 || !!o.financing_plan_id;
  const accCount2 = o.lines.filter((l) => !equipKeySet.has(l.kind) && l.kind !== "custom" && l.role !== "recommended").length;
  return (
    <Card className="overflow-hidden">
      <div className={cn("flex flex-wrap items-center justify-between gap-2 px-4 py-3", TIER_HEAD[o.tier] ?? TIER_HEAD.good)}>
        <div className="flex items-center gap-2">
          <span className="text-[15px] font-extrabold uppercase tracking-wide">{o.label ?? o.tier}</span>
          <button
            onClick={onToggleRecommend}
            title={recommended ? "Recommended — the customer sees this highlighted" : "Mark as the recommended option"}
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors",
              recommended ? "bg-amber-300 text-amber-900" : "bg-black/20 text-white/70 hover:bg-black/30 hover:text-white",
            )}
          >
            <Star className={cn("h-3 w-3", recommended && "fill-amber-700")} />
            {recommended ? "Recommended" : "Recommend"}
          </button>
        </div>
        <div className="inline-flex rounded-full bg-black/25 p-0.5">
          {(brands.data ?? []).map((b) => (
            <button
              key={b.id}
              onClick={() => setBrand.mutate({ id: o.id, brand: b.name })}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-semibold transition-colors",
                o.brand === b.name ? "bg-white text-slate-900" : "text-white/70 hover:text-white",
              )}
            >
              {b.name}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3 p-3">
        {cats.length === 0 && (
          <div className="rounded-lg border border-dashed border-hairline px-3 py-4 text-center text-xs text-slate-400">
            No slots on this quote. Use “Add slot” above, or add categories in Settings → Quotes.
          </div>
        )}
        {cats.map((cat) => {
          // QUOTE-POLISH-10: Recommended Add-Ons are NOT in the package — they
          // live on the shelf below and only the homeowner moves one in.
          const lines = o.lines.filter((l) => l.kind === cat.key && l.role !== "recommended");
          const Icon = catIcon(cat.icon);
          const isAccessoryLike = cat.kind === "accessory" || cat.kind === "enhancement";
          // Sub-category cards live in the catalog as children of this slot (managed in
          // Settings → Quotes). Accessory/enhancement WITH children → the proposal-app fan-out.
          const children = isAccessoryLike
            ? allCats.filter((c) => c.parent_key === cat.key && c.is_active).sort((a, b) => a.sort - b.sort)
            : [];
          if (isAccessoryLike && children.length > 0) {
            return (
              <div key={cat.key}>
                <div className="mb-1.5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-600">
                  <Icon className="h-3.5 w-3.5" /> {cat.label}
                  <InfoTip label="About accessories">Accessories picked here are agreed-to gear: they land on the package as Included at $0, with their value shown struck. Equipment is always priced.</InfoTip>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {children.map((sub) => (
                    <button
                      key={sub.key}
                      onClick={() => onAdd(cat, { matchField: sub.pricebook_match, value: sub.pricebook_subcategory, label: sub.label })}
                      className="flex items-center justify-between gap-1 rounded-lg border border-hairline bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 transition-colors hover:border-brand-300 hover:bg-brand-50/50"
                    >
                      <span className="truncate">+ {sub.label}</span>
                      <Plus className="h-3 w-3 shrink-0 text-slate-400" />
                    </button>
                  ))}
                </div>
                {lines.length > 0 && (
                  <ul className="mt-1.5 space-y-1.5">
                    {lines.map((l) => (
                      <LineRow key={l.id} l={l} option={o} showQty onRemove={() => remove.mutate(l.id)} />
                    ))}
                  </ul>
                )}
              </div>
            );
          }
          // Equipment slot (or an accessory/enhancement on a non-Accessory pricebook): the empty
          // block is itself the picker; "+ Add another" appears once one is chosen.
          return (
            <div key={cat.key}>
              <div className="mb-1 flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-600">
                  <Icon className="h-3.5 w-3.5" /> {cat.label}
                </span>
                {lines.length > 0 && (
                  <button
                    onClick={() => onAdd(cat)}
                    title={`Add another ${cat.label.toLowerCase()}`}
                    className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700 transition-colors hover:bg-brand-100"
                  >
                    <Plus className="h-3 w-3" /> Add Another
                  </button>
                )}
              </div>
              {lines.length === 0 ? (
                <button
                  onClick={() => onAdd(cat)}
                  className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-hairline px-3 py-3.5 text-xs font-semibold text-slate-500 transition-colors hover:border-brand-300 hover:bg-brand-50/40 hover:text-brand-700"
                >
                  <Plus className="h-3.5 w-3.5" /> Add a {cat.label}
                </button>
              ) : (
                <ul className="space-y-1.5">
                  {lines.map((l) => (
                    <LineRow key={l.id} l={l} option={o} showQty={cat.kind !== "equipment"} onRemove={() => remove.mutate(l.id)} />
                  ))}
                </ul>
              )}
            </div>
          );
        })}

        {/* Custom one-time items — name + price, any quantity (proposal-app parity) */}
        <CustomItemsBlock o={o} />
      </div>

      <div className="space-y-2.5 border-t border-hairline px-4 py-3">
        {/* QUOTE-POLISH-05: utility rebates, itemized — CenterPoint for the
            furnace AND Xcel for the A/C on the same collection (his app and the
            phone wizard both do this; the office builder now matches). */}
        <RebatesBlock o={o} utilities={utils.data ?? []} />

        {/* Discounts — multiple, named, % or $ (the proposal-app "custom-named discounts") */}
        <DiscountsBlock o={o} />

        {/* CALCULATED PKG PRICE (his per-tier footer): the number the customer
            pays, loud; rebates shown as money BACK, never subtracted from the
            contract (Seth 2026-08-27). */}
        <div className="-mx-4 border-t border-slate-100 bg-slate-50/70 px-4 py-2.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-400">
              Calculated Pkg Price
            </span>
            <div className="text-right leading-tight">
              {total < subtotal && (
                <div className="text-[11px] tabular-nums text-slate-400 line-through">{money(subtotal)}</div>
              )}
              <span className="text-2xl font-bold tabular-nums text-slate-900 [font-family:var(--font-display)]">
                {money(total)}
              </span>
            </div>
          </div>
          {(o.rebate_total ?? 0) > 0 && (
            <div className="mt-0.5 text-right text-[11px] font-semibold text-emerald-700">
              {money(o.rebate_total)} back in utility rebates → {money(Math.max(0, total - (o.rebate_total ?? 0)))} net
            </div>
          )}
        </div>

        {/* Financing — plan + estimated monthly the customer would see */}
        <div className="flex items-center gap-2 border-t border-slate-100 pt-2">
          <span className="w-[72px] shrink-0 text-xs font-semibold text-slate-600">Financing</span>
          <select
            value={o.financing_plan_id ?? ""}
            onChange={(e) => updateOption.mutate({ id: o.id, financing_plan_id: e.target.value || null })}
            className="min-w-0 flex-1 rounded-md border border-hairline bg-white px-2 py-1 text-xs text-slate-700"
          >
            <option value="">No financing</option>
            {(plans.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {plan && monthly > 0 && (
            <span className="shrink-0 text-xs font-bold tabular-nums text-brand-700">
              ≈ {money(monthly)}/mo
            </span>
          )}
        </div>

        {/* QUOTE-POLISH-07: each UNIT carries its own warranty — click the green
            chip on an equipment line to change that unit's parts / labor years. */}

        {/* QUOTE-POLISH-01 → 03: fill it out once, copy it to the other collections
            (Seth: "Mirror isn't descriptive enough for my users"). */}
        {others.length > 0 && (hasMoney || accCount2 > 0) && (
          <div className="border-t border-slate-100 pt-2.5">
            <div className="mb-1.5 text-xs font-semibold text-slate-600">
              Copy to the Other {others.length === 1 ? "Collection" : `${others.length} Collections`}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {hasMoney && (
                <button
                  type="button"
                  disabled={copyExtras.isPending}
                  title="Copies this collection's utility rebate, named discounts and financing plan onto the other collections — their own discounts are replaced so they match"
                  onClick={() => copyExtras.mutate({ from: o, targets: others, money: true, equipKeys: equipKeyList })}
                  className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-e1 transition-colors hover:border-brand-300 hover:text-brand-700 disabled:opacity-50"
                >
                  <Copy className="h-3.5 w-3.5 text-brand-500" /> Copy Rebate &amp; Discounts
                </button>
              )}
              {accCount2 > 0 && (
                <button
                  type="button"
                  disabled={copyExtras.isPending}
                  title="Copies this collection's accessories onto the other collections — anything they already have is skipped, nothing is doubled"
                  onClick={() => copyExtras.mutate({ from: o, targets: others, accessories: true, equipKeys: equipKeyList })}
                  className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-e1 transition-colors hover:border-brand-300 hover:text-brand-700 disabled:opacity-50"
                >
                  <Copy className="h-3.5 w-3.5 text-brand-500" /> Copy accessories
                </button>
              )}
              {copyExtras.isSuccess && !copyExtras.isPending && (
                <span className="text-xs font-semibold text-emerald-700">Copied ✓</span>
              )}
            </div>
          </div>
        )}

        {/* Protect — enroll / renew a PLUS membership on accept (service quotes) */}
        <MembershipBlock o={o} quote={quote} allCats={allCats} />
      </div>
    </Card>
  );
}

/** Month label like "Mar 2027" from an ISO date. */
function monLabel(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

/**
 * Protect = a PLUS membership on the option. The tech picks the plan, term, and
 * the "first check" (season + year) — on accept, Pegasus auto-enrolls (or
 * renews/extends) the customer, folds in this option's accessories, and lays out
 * the tune-up schedule from that first check. State-aware: a member with runway
 * left just shows "already covered" (the accept trigger won't double-enroll);
 * one expiring soon / lapsed flips to "renew & extend". Service quotes only.
 */
function MembershipBlock({ o, quote, allCats }: { o: QuoteOptionFull; quote: Quote; allCats: QuoteCategory[] }) {
  const updateOption = useUpdateQuoteOption();
  const catalog = usePlanPriceCatalog();
  const status = useCustomerMembershipStatus(quote.custno);
  if (quote.quote_type !== "service") return null;
  // PLAN-OFFER-01: the proposal-level Plan Offer block is the way forward; this
  // per-option Protect block stays only for options that already carry one.
  if (!o.plan_base_id) return null;

  const bases = (catalog.data ?? []).filter((p) => p.kind === "base" && p.active);
  const enabled = !!o.plan_base_id;
  const thisYear = new Date().getFullYear();
  const season = o.first_check_season ?? "heating";
  const year = o.first_check_year ?? thisYear;
  const st = status.data;

  // Accessories already on this option fold into the membership (truck-parts list).
  const accCount = o.lines.filter(
    (l) => l.kind === "custom" || allCats.some((c) => c.key === l.kind && (c.kind === "accessory" || c.kind === "enhancement")),
  ).length;

  // Status chip — tells the tech whether Protect means enroll / renew / already covered.
  const chip = (() => {
    if (!st || st.state === "none") return { text: "Not a member — Protect enrolls them", cls: "bg-slate-100 text-slate-600" };
    if (st.state === "active") return { text: `Member through ${monLabel(st.horizon)} — covered today`, cls: "bg-emerald-50 text-emerald-700" };
    if (st.state === "expiring") return { text: `Expires ${monLabel(st.horizon)} — renew & extend`, cls: "bg-amber-50 text-amber-700" };
    return { text: `Lapsed ${monLabel(st.horizon)} — renew`, cls: "bg-rose-50 text-rose-700" };
  })();

  const toggle = () =>
    updateOption.mutate({
      id: o.id,
      plan_base_id: enabled ? null : (bases[0]?.id ?? null),
      plan_term: enabled ? null : 1,
      first_check_season: enabled ? null : "heating",
      first_check_year: enabled ? null : thisYear,
    });

  return (
    <div className="border-t border-slate-100 pt-2.5">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-600">
          <ShieldCheck className="h-3.5 w-3.5" /> Protect (Membership)
        </span>
        <button
          onClick={toggle}
          className={cn(
            "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide transition-colors",
            enabled ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200",
          )}
        >
          {enabled ? "On" : "Add"}
        </button>
      </div>

      {/* Always show the customer's current coverage so the tech frames it right. */}
      <div className={cn("mt-1.5 rounded-md px-2 py-1 text-[11px] font-medium", chip.cls)}>{chip.text}</div>

      {enabled && (
        <div className="mt-2 space-y-2">
          <select
            value={o.plan_base_id ?? ""}
            onChange={(e) => updateOption.mutate({ id: o.id, plan_base_id: e.target.value || null })}
            className="w-full rounded-md border border-hairline bg-white px-2 py-1 text-xs text-slate-700"
          >
            <option value="">Choose a plan…</option>
            {bases.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}{b.p1 != null ? ` — ${money(b.p1)}/yr` : ""}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-2">
            <span className="w-[68px] shrink-0 text-[11px] font-medium text-slate-500">Term</span>
            <select
              value={o.plan_term ?? 1}
              onChange={(e) => updateOption.mutate({ id: o.id, plan_term: Number(e.target.value) || 1 })}
              className="flex-1 rounded-md border border-hairline bg-white px-2 py-1 text-xs text-slate-700"
            >
              {[1, 2, 3, 5].map((t) => (
                <option key={t} value={t}>{t} {t === 1 ? "year" : "years"}</option>
              ))}
            </select>
          </div>

          {/* First check — which seasonal tune-up comes first + the year. */}
          <div className="flex items-center gap-2">
            <span className="w-[68px] shrink-0 text-[11px] font-medium text-slate-500">First check</span>
            <div className="inline-flex flex-1 rounded-md border border-hairline p-0.5">
              <button
                onClick={() => updateOption.mutate({ id: o.id, first_check_season: "heating" })}
                className={cn("flex-1 rounded px-1.5 py-0.5 text-[11px] font-semibold transition-colors",
                  season === "heating" ? "bg-orange-500 text-white" : "text-slate-500 hover:text-slate-700")}
              >
                Fall · Furnace
              </button>
              <button
                onClick={() => updateOption.mutate({ id: o.id, first_check_season: "cooling" })}
                className={cn("flex-1 rounded px-1.5 py-0.5 text-[11px] font-semibold transition-colors",
                  season === "cooling" ? "bg-sky-500 text-white" : "text-slate-500 hover:text-slate-700")}
              >
                Spring · A/C
              </button>
            </div>
            <MoneyField
              className="w-16 shrink-0"
              prefix={null}
              value={year}
              onCommit={(v) => updateOption.mutate({ id: o.id, first_check_year: v || thisYear })}
              title="Year of the first covered check"
            />
          </div>

          <p className="text-[11px] leading-snug text-slate-400">
            On accept: {st && st.state === "active"
              ? "already covered — no second plan is created."
              : st && (st.state === "expiring" || st.state === "lapsed")
                ? "renews & extends their plan from the current expiration."
                : "enrolls the customer in this plan."}
            {accCount > 0 && ` ${accCount} accessor${accCount === 1 ? "y" : "ies"} on this option fold in.`}
          </p>
        </div>
      )}
    </div>
  );
}

/* ── PLAN-OFFER-01 (34i101, mock B / B2): one Plan Offer for the whole proposal.
   The office picks the catalog plan, the default term, whether the homeowner
   ticks it or it is included in the price, and the first covered check. The
   catalog's term prices are shown here and snapshotted by the database the
   moment the proposal goes out (pegasus_plan_offer_create) — a later catalog
   change never re-prices a live offer. On a NEW-EQUIPMENT proposal the plan is
   a straight add: switching it on asks the tech once — nothing, a bundle
   discount line, or the first year included — equipment lines never move on
   their own. ─────────────────────────────────────────────────────────────── */
const PLAN_TERMS_DEFAULT = [1, 3, 5];
/** PROPOSAL-EQUIPMENT-02 / BUILDER-FLOW-01: the unit(s) this proposal is about — the repair's
 *  subject, or what a new system replaces — as rows the office ticks. The tech's wizard fills
 *  this from "What are you working on?"; the homeowner page leads with it. */
function WorkingOnRows({ quote, locked, units, tone }: { quote: Quote; locked: boolean; units: ClassifiedEquip[]; tone: "work" | "plan" }) {
  const updateQuote = useUpdateQuote();
  const [all, setAll] = useState(false);
  const on = new Set(quote.work_units ?? []);
  if (units.length === 0) return <div className="text-xs text-slate-400">No equipment on record at this address.</div>;
  const toggle = (counter: string) => {
    if (locked) return;
    const next = on.has(counter) ? [...on].filter((c) => c !== counter) : [...on, counter];
    updateQuote.mutate({ id: quote.id, work_units: next.length ? next : null });
  };
  // The marked units first; with many on record, the rest wait behind one link.
  const ordered = [...units].sort((a, b) => Number(on.has(b.counter)) - Number(on.has(a.counter)));
  const LIMIT = 4;
  const shown = all || ordered.length <= LIMIT ? ordered : ordered.slice(0, Math.max(LIMIT, on.size));
  const hidden = ordered.length - shown.length;
  return (
    <>
      <div className="grid gap-1.5 sm:grid-cols-2">
        {shown.map((u) => {
          const loc = (u.equip_loc ?? "").trim();
          return (
            <UnitPickRow key={u.counter} kind={u.category} label={u.categoryLabel}
              loc={loc ? loc.charAt(0).toUpperCase() + loc.slice(1).toLowerCase() : null}
              brand={makeFromMfg(u.mfg)} model={(u.model ?? "").trim() || null} serial={(u.serial ?? "").trim() || null}
              year={installDateInfo(u.install_date)?.year ?? null}
              on={on.has(u.counter)} tone={tone} disabled={locked} onToggle={() => toggle(u.counter)} />
          );
        })}
      </div>
      {(hidden > 0 || all) && ordered.length > LIMIT && (
        <button type="button" onClick={() => setAll((v) => !v)} className="mt-2 text-xs font-semibold text-slate-500 hover:text-slate-800">
          {all ? "Show less" : `Show the other ${hidden}`}
        </button>
      )}
    </>
  );
}

function PlanOfferBlock({ quote, locked, expanded, onExpand }: { quote: Quote; locked: boolean; expanded?: boolean; onExpand?: (v: boolean) => void }) {
  const updateQuote = useUpdateQuote();
  const catalog = usePlanPriceCatalog();
  const status = useCustomerMembershipStatus(quote.custno);
  const bases = (catalog.data ?? []).filter((p) => p.kind === "base" && p.active);
  const on = !!quote.plan_offer_base_id;
  const base = bases.find((b) => b.id === quote.plan_offer_base_id) ?? null;
  const terms = quote.plan_offer_terms && quote.plan_offer_terms.length ? quote.plan_offer_terms : PLAN_TERMS_DEFAULT;
  const term = quote.plan_offer_default_term ?? terms[0] ?? 1;
  const mode = quote.plan_offer_mode ?? "optional";
  const isInstall = quote.quote_type !== "service";
  const thisYear = new Date().getFullYear();
  const season = quote.plan_offer_first_check_season ?? (isInstall ? "cooling" : "heating");
  const year = quote.plan_offer_first_check_year ?? (isInstall && season === "cooling" ? thisYear + (new Date().getMonth() >= 7 ? 1 : 0) : thisYear);
  const [prompt, setPrompt] = useState<null | { bundle: "none" | "discount" | "first_year_free"; amount: number }>(null);
  const st = status.data;
  // PLAN-OFFER-SYSTEMS: the systems this plan covers — from the home's equipment record
  // (kind + room, never model/serial); the saved on/off choices ride the quote. The price
  // is per system: every extra one at the Plan Builder's second-system discount.
  const planCatalog = usePlanCatalog();
  const equipRows = useCustomerEquipment(quote.custno ?? undefined, quote.locno ?? null).data;
  const equipOverrides = useEquipmentOverrides(quote.custno ?? undefined, quote.locno ?? null).data;
  const covers: PlanOfferCover[] = useMemo(
    () => planCoversFor(equipRows, equipOverrides, quote.plan_offer_covers ?? null),
    [equipRows, equipOverrides, quote.plan_offer_covers],
  );
  const systems = Math.max(1, quote.plan_offer_systems ?? planSystemsCount(covers));
  const secondDisc = Number(planCatalog.data?.config?.second_system_discount ?? 0.3);
  const mult = planSystemsMultiplier(systems, secondDisc);
  const setCover = (counter: string, onNow: boolean) => {
    if (locked) return;
    const next = covers.map((c) => (c.counter === counter ? { ...c, on: onNow } : c));
    updateQuote.mutate({ id: quote.id, plan_offer_covers: next, plan_offer_systems: planSystemsCount(next) });
  };
  const priceFor = (b: typeof bases[number] | null, t: number): number | null => {
    if (!b) return null;
    const p = ({ 1: b.p1, 2: b.p2, 3: b.p3, 5: b.p5, 10: b.p10 } as Record<number, number | null | undefined>)[t];
    const base1 = p ?? (b.p1 != null ? b.p1 * t : null);
    return base1 == null ? null : Math.round(base1 * mult * 100) / 100;
  };
  const chip = (() => {
    if (!st || st.state === "none") return { text: "Not a member", sub: "accepting enrolls them", cls: "text-slate-600" };
    if (st.state === "active") return { text: `Member through ${monLabel(st.horizon)}`, sub: "accepting renews & extends from there", cls: "text-emerald-700" };
    if (st.state === "expiring") return { text: `Expires ${monLabel(st.horizon)}`, sub: "accepting renews & extends", cls: "text-amber-700" };
    return { text: `Lapsed ${monLabel(st.horizon)}`, sub: "accepting re-enrolls them", cls: "text-rose-700" };
  })();
  const expiresText = (() => {
    const from = quote.sent_at ? new Date(quote.sent_at) : null;
    const d = from ? new Date(from.getTime() + 60 * 864e5) : null;
    return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "60 days from sending";
  })();
  const turnOn = (bundle: "none" | "discount" | "first_year_free" = "none", amount: number | null = null) =>
    updateQuote.mutate({
      id: quote.id,
      plan_offer_base_id: quote.plan_offer_base_id ?? bases[0]?.id ?? null,
      plan_offer_mode: quote.plan_offer_mode ?? (bundle === "first_year_free" ? "included" : "optional"),
      plan_offer_default_term: quote.plan_offer_default_term ?? (bundle === "first_year_free" ? 1 : (terms.includes(3) ? 3 : terms[0] ?? 1)),
      plan_offer_terms: quote.plan_offer_terms ?? PLAN_TERMS_DEFAULT,
      plan_offer_first_check_season: season,
      plan_offer_first_check_year: year,
      plan_offer_bundle: isInstall ? bundle : null,
      plan_offer_bundle_amount: isInstall && bundle === "discount" ? amount : null,
      plan_offer_covers: quote.plan_offer_covers ?? covers,
      plan_offer_systems: quote.plan_offer_systems ?? planSystemsCount(covers),
    });
  const turnOff = () => updateQuote.mutate({ id: quote.id, plan_offer_base_id: null, plan_offer_bundle: null, plan_offer_bundle_amount: null });
  const toggle = () => {
    if (locked) return;
    if (on) return turnOff();
    // PLAN-INSTALL-DEFAULT (Seth 2026-10-28, "GO with those"): on new equipment the
    // default is the first year included — a $0 plan line, renewing at the catalog
    // rate — because a $229–$916 checkbox competes with a $20,000 decision, the first
    // furnace check is covered by the install anyway, and a member in year one is the
    // renewal conversation in year two. "Just the plan" and the bundle discount stay
    // one tap away for the tech.
    if (isInstall) return setPrompt({ bundle: "first_year_free", amount: 150 });
    turnOn();
  };
  const seg = (active: boolean) =>
    cn("flex-1 rounded-md px-2 py-1 text-[11.5px] font-semibold transition-colors", active ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800");
  const switchBtn = (
    <button
      type="button"
      onClick={toggle}
      disabled={locked || updateQuote.isPending}
      aria-pressed={on}
      aria-label={on ? "Plan offered — turn off" : "Offer the plan"}
      className={cn("relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50", on ? "bg-emerald-600" : "bg-slate-300")}
    >
      <span className={cn("absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all", on ? "left-[22px]" : "left-0.5")} />
    </button>
  );
  const pct = (() => {
    const rd = (planCatalog.data?.config?.repair_discount ?? {}) as Record<string, number>;
    const keys = Object.keys(rd).map(Number).filter((k) => k <= term);
    const v = rd[String(term)] ?? (keys.length ? rd[String(Math.max(...keys))] : 0);
    return Math.round(Number(v ?? 0) * 100);
  })();
  const price = priceFor(base, term);
  // BUILDER-FLOW-01: one line until Change — the switch, the plan in six words, the price.
  if (!expanded && !prompt) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        {switchBtn}
        {on ? (
          <>
            <span className="text-[13.5px] text-slate-700">
              <b>Maintenance Plus</b>{base ? <span className="text-slate-500"> · {base.name}</span> : null} · {term} yr · <b>{systems} {systems === 1 ? "system" : "systems"}</b>
              {quote.plan_offer_bundle === "first_year_free" ? <> · <b>first year on us</b></> : price != null ? <> · <span className="tabular-nums">{money(price)}</span></> : null}
              {!isInstall && pct > 0 ? <span className="text-slate-500"> · member diagnostic and {pct}% off this repair</span> : null}
              {mode === "included" && quote.plan_offer_bundle !== "first_year_free" ? <span className="text-slate-500"> · included in the price</span> : null}
            </span>
            <button type="button" onClick={() => onExpand?.(true)} className="ml-auto text-xs font-semibold text-brand-700 hover:text-brand-900">Change ›</button>
          </>
        ) : (
          <span className="text-[13px] text-slate-400">Off</span>
        )}
      </div>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="flex items-start justify-between gap-3 px-4 pt-4">
        <div className="flex items-center gap-3">
          {switchBtn}
          <div className="text-[13.5px] font-bold text-slate-800">{on ? "Plan details" : "Offer a plan"}</div>
        </div>
        {on && !prompt && <button type="button" onClick={() => onExpand?.(false)} className="text-xs font-semibold text-brand-700 hover:text-brand-900">Done</button>}
      </div>

      {prompt && !on && (
        <div className="mx-4 mt-3 rounded-2xl border border-brand-200 bg-brand-50/40 p-4 shadow-[0_0_0_4px_rgba(37,99,235,.06)]">
          <div className="flex items-start gap-2.5">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-700"><Scale className="h-4 w-4" /></span>
            <div>
              <div className="text-sm font-extrabold text-slate-900">A plan on an install doesn't change the equipment price</div>
              <div className="text-xs text-slate-500">The homeowner would pay the collection plus the plan. Want to sweeten it?</div>
            </div>
          </div>
          <div className="mt-3 space-y-2">
            {([
              { v: "first_year_free", t: "First year on us", d: "A 1-year plan included in the price · renews at the catalog rate" },
              { v: "none", t: "Just the plan", d: "Offered at the catalog price · the homeowner ticks it and picks a term" },
              { v: "discount", t: "Plan bundle discount", d: "Off the install when they add the plan · shows as its own line" },
            ] as const).map((c) => (
              <label key={c.v} className={cn("flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5", prompt.bundle === c.v ? "border-brand-500 bg-brand-50" : "border-hairline bg-white")}>
                <input type="radio" name="plan-bundle" className="accent-brand-600" checked={prompt.bundle === c.v} onChange={() => setPrompt({ ...prompt, bundle: c.v })} />
                <span className="flex-1 text-sm">
                  <b>{c.t}</b>
                  <span className="block text-xs text-slate-500">{c.d}</span>
                </span>
                {c.v === "discount" && prompt.bundle === "discount" && (
                  <MoneyField className="w-24" value={prompt.amount} onCommit={(v) => setPrompt({ ...prompt, amount: v })} title="Bundle discount" />
                )}
              </label>
            ))}
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setPrompt(null)} className="rounded-full px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
            <button type="button" disabled={updateQuote.isPending} onClick={() => { turnOn(prompt.bundle, prompt.bundle === "discount" ? prompt.amount : null); setPrompt(null); }}
              className="rounded-full bg-brand-700 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-brand-800 disabled:opacity-50">Add the Plan Offer</button>
          </div>
        </div>
      )}

      {on && (
        <div className="px-4 pb-4">
          <div className="mt-4 grid gap-x-5 gap-y-3 sm:grid-cols-2">
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400">Plan</div>
              <select
                value={quote.plan_offer_base_id ?? ""}
                disabled={locked}
                onChange={(e) => updateQuote.mutate({ id: quote.id, plan_offer_base_id: e.target.value || null })}
                className="mt-1.5 w-full rounded-lg border border-hairline bg-white px-2.5 py-1.5 text-sm text-slate-800"
              >
                {bases.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400">Default Term</div>
              <div className="mt-1.5 inline-flex w-full rounded-lg border border-hairline bg-white p-0.5">
                {terms.map((t) => (
                  <button key={t} type="button" disabled={locked} onClick={() => updateQuote.mutate({ id: quote.id, plan_offer_default_term: t })} className={seg(term === t)}>{t} yr</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400">How It's Offered</div>
              <div className="mt-1.5 inline-flex w-full rounded-lg border border-hairline bg-white p-0.5">
                <button type="button" disabled={locked} onClick={() => updateQuote.mutate({ id: quote.id, plan_offer_mode: "optional" })} className={seg(mode === "optional")}>Optional · Homeowner Ticks It</button>
                <button type="button" disabled={locked} onClick={() => updateQuote.mutate({ id: quote.id, plan_offer_mode: "included" })} className={seg(mode === "included")}>Included In The Price</button>
              </div>
            </div>
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400">First Check</div>
              <div className="mt-1.5 flex items-center gap-2">
                <div className="inline-flex flex-1 rounded-lg border border-hairline bg-white p-0.5">
                  <button type="button" disabled={locked} onClick={() => updateQuote.mutate({ id: quote.id, plan_offer_first_check_season: "heating" })} className={cn(seg(season === "heating"), season === "heating" && "bg-orange-500")}>Furnace · Fall</button>
                  <button type="button" disabled={locked} onClick={() => updateQuote.mutate({ id: quote.id, plan_offer_first_check_season: "cooling" })} className={cn(seg(season === "cooling"), season === "cooling" && "bg-sky-500")}>A/C · Spring</button>
                </div>
                <MoneyField className="w-20 shrink-0" prefix={null} value={year} onCommit={(v) => updateQuote.mutate({ id: quote.id, plan_offer_first_check_year: v || thisYear })} title="Year of the first covered check" />
              </div>
              {isInstall && <div className="mt-1 text-[11px] text-slate-400">Auto from the install season — change if the furnace isn't part of the job</div>}
            </div>
          </div>

          {/* PLAN-OFFER-SYSTEMS: which units the plan covers — the home's record, by kind and room. */}
          <div className="mt-4">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400">Covers</div>
              <div className="text-[11px] font-semibold text-slate-600">{systems} system{systems === 1 ? "" : "s"}{systems > 1 ? ` · each extra ${Math.round(secondDisc * 100)}% off` : ""}</div>
            </div>
            {covers.length === 0 ? (
              <div className="mt-1.5 text-xs text-slate-400">No systems on the equipment record yet — priced as one system; add units from the 360's Equipment tab.</div>
            ) : (
              // r2 (Seth: "I hate the pills"): one row per unit — kind · room, make + model, the serial as data, the tick at the end.
              <div className="mt-1.5 grid gap-1.5 sm:grid-cols-2">
                {covers.map((c) => (
                  <UnitPickRow key={c.counter} kind={c.kind} label={c.kind === "ac" ? "A/C" : c.label} loc={c.loc}
                    brand={c.brand ?? null} model={c.model ?? null} serial={c.serial ?? null} year={c.year ?? null}
                    on={c.on} tone="plan" disabled={locked} onToggle={() => setCover(c.counter, !c.on)} />
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 grid gap-2.5 sm:grid-cols-3">
            <div className="rounded-xl bg-slate-50 px-3 py-2.5">
              <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400">Prices Shown</div>
              <div className="mt-1 text-sm font-bold tabular-nums text-slate-800">
                {terms.map((t, i) => { const p = priceFor(base, t); return <span key={t}>{i > 0 ? " · " : ""}{t} yr {p != null ? money(p) : "—"}</span>; })}
              </div>
              <div className="text-[11px] text-slate-400">{systems > 1 ? `${systems} systems · ` : ""}from the Plan Catalog · snapshotted when sent</div>
            </div>
            <div className="rounded-xl bg-slate-50 px-3 py-2.5">
              <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400">This Customer</div>
              <div className={cn("mt-1 text-sm font-bold", chip.cls)}>{chip.text}</div>
              <div className="text-[11px] text-slate-400">{chip.sub}</div>
            </div>
            <div className="rounded-xl bg-slate-50 px-3 py-2.5">
              <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400">Expires</div>
              <div className="mt-1 text-sm font-bold text-slate-800">{expiresText}{quote.sent_at ? " · 60 days" : ""}</div>
              <div className="text-[11px] text-slate-400">with the proposal · then Follow-Ups → Resend</div>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            {isInstall ? (
              <>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-600">Install quotes · the first furnace check is covered by the install</span>
                {quote.plan_offer_bundle === "discount" && <span className="rounded-full bg-brand-50 px-2.5 py-1 font-semibold text-brand-700">Bundle discount {money(Number(quote.plan_offer_bundle_amount ?? 0))} when they add the plan</span>}
                {quote.plan_offer_bundle === "first_year_free" && <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">First year on us · included in the price</span>}
              </>
            ) : (
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">Repair quotes · the member savings line shows automatically</span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

/** QUOTE-POLISH-01: the money + name inputs used to write to the DB on every
 *  keystroke — the react-query refetch then re-rendered the input mid-word and
 *  ATE what was being typed (Seth: "$450 kept overwriting or not entering").
 *  These fields hold a local draft while focused and commit ONCE on blur or
 *  Enter; idle, they always track the database value. */
function MoneyField({ value, onCommit, className, inputClassName, placeholder = "0", prefix = "$", title }: {
  value: number;
  onCommit: (v: number) => void;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  prefix?: string | null;
  title?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const v = draft.trim() === "" ? 0 : Number(draft);
    setDraft(null);
    if (!Number.isNaN(v) && v !== value) onCommit(v);
  };
  return (
    <span className={cn("relative inline-flex", className)} title={title} onClick={(e) => e.stopPropagation()}>
      {prefix && <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">{prefix}</span>}
      <input
        type="number"
        inputMode="decimal"
        value={draft ?? (value ? String(value) : "")}
        placeholder={placeholder}
        onFocus={() => setDraft(value ? String(value) : "")}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
        className={cn(
          "w-full rounded-md border border-hairline bg-white py-1 pr-2 text-right text-xs tabular-nums focus:border-brand-400 focus:outline-none",
          prefix ? "pl-5" : "pl-2",
          inputClassName,
        )}
      />
    </span>
  );
}

/** Same commit-on-blur pattern for text (discount names). */
function TextField({ value, onCommit, placeholder, className }: { value: string; onCommit: (v: string) => void; placeholder?: string; className?: string }) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const v = draft;
    setDraft(null);
    if (v !== value) onCommit(v);
  };
  return (
    <input
      value={draft ?? value}
      placeholder={placeholder}
      onFocus={() => setDraft(value)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
      className={className}
    />
  );
}

/** Compact −N+ stepper for line quantities (accessories, parts, custom items). */
function QtyStepper({ qty, onChange }: { qty: number; onChange: (q: number) => void }) {
  return (
    <div className="flex shrink-0 items-center rounded-md border border-hairline" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => onChange(Math.max(1, qty - 1))}
        className="px-1.5 py-0.5 text-slate-400 transition-colors hover:text-slate-700"
        aria-label="Decrease quantity"
      >
        −
      </button>
      <span className="min-w-[16px] text-center text-[11px] font-semibold tabular-nums text-slate-700">{qty}</span>
      <button
        onClick={() => onChange(qty + 1)}
        className="px-1.5 py-0.5 text-slate-400 transition-colors hover:text-slate-700"
        aria-label="Increase quantity"
      >
        +
      </button>
    </div>
  );
}

/* QUOTE-POLISH-10 (Seth, after a miss on my side): a package accessory has TWO
   independent settings — exactly his app's pills:
     INCLUDED ↔ CHARGEABLE   does the homeowner pay for it? Included = $0 to them,
                             value still shown (line discount = full value — the
                             netting every pricing engine reads); Chargeable = priced.
     OPTIONAL ↔ REQUIRED     can the homeowner take it OFF? Optional = in the
                             package, pre-checked on the proposal, removable (the
                             price drops if they do); Required = locked in.
   Four honest combinations. Neither setting on its own moves the package price
   except Included (QUOTE-POLISH-13, Seth: "Required is changing the price of
   the quote when it shouldn't" — it was, because Optional lines used to sit
   outside the price as opt-ins; now the only opt-in is the Recommended Add-Ons
   shelf below). Drawn as two small segmented switches so BOTH choices are
   visible and the lit one is the setting (his second complaint: the old pills
   were huge and you couldn't tell which state was on). */
const WARRANTY_YEARS = Array.from({ length: 20 }, (_, i) => i + 1);

function RideSwitch<T extends string>({ value, onChange, disabled, options }: {
  value: T;
  onChange: (v: T) => void;
  disabled?: boolean;
  options: Array<{ v: T; label: ReactNode; on: string; title: string }>;
}) {
  return (
    <span role="radiogroup" className="inline-flex h-[22px] shrink-0 items-stretch overflow-hidden rounded-md border border-slate-200 bg-white text-[10px] font-bold leading-none">
      {options.map((o, i) => {
        const on = o.v === value;
        return (
          <button
            key={o.v}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            title={o.title}
            onClick={() => { if (!on) onChange(o.v); }}
            className={cn(
              "inline-flex items-center gap-1 px-1.5 transition-colors disabled:cursor-default",
              i > 0 && "border-l border-slate-200",
              on ? o.on : "text-slate-400 hover:bg-slate-50 hover:text-slate-700",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </span>
  );
}

function LineRow({ l, option, onRemove, showQty: showQtyProp }: { l: QuoteLine; option: QuoteOptionFull; onRemove: () => void; showQty?: boolean }) {
  const update = useUpdateQuoteLine();
  // QUOTE-POLISH-07 (Seth: "each unit should carry their own — an SLP99 at 12/7
  // with an ML13 at 5/2"): the green chip is THIS unit's warranty. Click it →
  // two year pickers in its place; a change saves on the line at once and the
  // chip comes back with the new numbers. "Standard" = what the pricebook says
  // for this model (else the tier standard).
  const [editWarranty, setEditWarranty] = useState(false);
  const [resetting, setResetting] = useState(false);
  useEffect(() => {
    if (!editWarranty) return;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") setEditWarranty(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [editWarranty]);
  const w = lineWarranty(l, option);
  const commitWarranty = (parts: number, labor: number) => {
    // Years win; a legacy text on the line is cleared so it can't disagree.
    update.mutate({ id: l.id, warranty_parts_years: parts, warranty_labor_years: labor, warranty: null });
    setEditWarranty(false);
  };
  const resetWarranty = async () => {
    setResetting(true);
    try {
      const it = l.item_id ? await fetchItemById(l.item_id) : null;
      update.mutate({
        id: l.id,
        warranty_parts_years: it?.warranty_parts_years ?? null,
        warranty_labor_years: it?.warranty_labor_years ?? null,
        warranty: null,
      });
    } finally {
      setResetting(false);
      setEditWarranty(false);
    }
  };
  const showQty = showQtyProp ?? (l.kind === "accessory" || l.kind === "custom");
  const isEquipment = showQtyProp === false;
  // "$0 Included" = discount equals the full value. Qty/price edits keep that
  // honest (see QtyStepper / MoneyField below).
  const isInc = l.discount > 0 && Math.abs(l.discount - l.qty * l.unit_price) < 0.005;
  const choice = l.role === "optional" || l.role === "recommended";
  const toggleIncluded = (v: "inc" | "chg") => update.mutate({ id: l.id, discount: v === "inc" ? l.qty * l.unit_price : 0 });
  const toggleRequired = (v: "opt" | "req") => update.mutate({ id: l.id, role: v === "opt" ? "optional" : "required" });
  const warrantyText = isEquipment ? lineWarrantyLabel(l, option) : "";
  return (
    <li className={cn("group rounded-xl border bg-white px-3 py-2", choice ? "border-dashed border-brand-300 bg-brand-50/20" : "border-hairline")}>
      {/* Row 1 — WHAT it is (the part Seth couldn't see): photo, name, model */}
      <div className="flex items-center gap-2.5">
        {l.image_url ? (
          <img src={l.image_url} alt="" className="h-10 w-10 shrink-0 rounded-md object-contain" />
        ) : (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-100 text-[9px] font-bold text-slate-400">HV</div>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold leading-snug text-slate-800">{l.description ?? l.model ?? "Item"}</div>
          <div className="flex items-center gap-1.5 truncate text-xs text-slate-400">
            {/* The code line is for EQUIPMENT (the model number is the product). An accessory's
                description already names it; its part/import key stays off the row (QUOTE-POLISH-15). */}
            {l.kind === "custom" ? (l.qty > 1 ? `${money(l.unit_price)} each` : null) : (isEquipment && l.model && l.model !== l.description ? l.model : null)}
            {l.website_url && (
              <a href={l.website_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-brand-500 hover:text-brand-700" title="Manufacturer page">
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
        <button
          onClick={onRemove}
          className="shrink-0 rounded-md p-1 text-slate-300 opacity-0 transition-all hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100"
          title="Remove from this collection"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      {/* Row 2 — HOW it rides: role · qty · price · total (equipment: warranty · price) */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {showQty && (
          <span className="inline-flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <RideSwitch<"inc" | "chg">
              value={isInc ? "inc" : "chg"}
              onChange={toggleIncluded}
              disabled={update.isPending}
              options={[
                { v: "inc", label: "Included", on: "bg-emerald-600 text-white", title: "Included — the homeowner pays $0 for it; its value shows struck through." },
                { v: "chg", label: "Chargeable", on: "bg-slate-800 text-white", title: "Chargeable — the homeowner pays this price; it is in the package price." },
              ]}
            />
            <RideSwitch<"opt" | "req">
              value={choice ? "opt" : "req"}
              onChange={toggleRequired}
              disabled={update.isPending}
              options={[
                { v: "opt", label: "Optional", on: "bg-slate-800 text-white", title: "Optional — in the package and in its price, but the homeowner can take it off on the proposal (the price drops by this line)." },
                { v: "req", label: <><Lock className="h-2.5 w-2.5" /> Required</>, on: "bg-amber-400 text-amber-950", title: "Required — locked in the package; the homeowner can't take it off." },
              ]}
            />
          </span>
        )}
        {isEquipment && warrantyText && !editWarranty && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setEditWarranty(true); }}
            title={w.source === "unit" ? "This unit's warranty — click to change its parts / labor years" : w.source === "text" ? "This unit's warranty (set on the phone) — click to change it" : "Standard warranty for this unit — click to set its own parts / labor years"}
            className={cn(
              "inline-flex h-7 items-center gap-1 rounded-md border px-2 text-xs font-semibold transition-colors",
              w.source === "unit" || w.source === "text"
                ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:border-emerald-400 hover:bg-emerald-100"
                : "border-emerald-200 bg-emerald-50/70 text-emerald-700 hover:border-emerald-400 hover:bg-emerald-100",
            )}
          >
            <ShieldCheck className="h-3.5 w-3.5" /> {warrantyText}
            <ChevronDown className="h-3 w-3 opacity-60" />
          </button>
        )}
        {isEquipment && editWarranty && (
          <span
            className="inline-flex h-7 items-center gap-1 rounded-md border border-emerald-400 bg-white px-1.5 text-xs font-semibold text-emerald-800 shadow-sm"
            title="This unit only — the other units keep their own warranty"
            onClick={(e) => e.stopPropagation()}
          >
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
            <select
              autoFocus
              value={w.parts}
              onChange={(e) => commitWarranty(Number(e.target.value), w.labor)}
              className="h-6 rounded border border-emerald-200 bg-white px-1 text-xs font-bold text-emerald-800 focus:border-emerald-500 focus:outline-none"
              title="Parts warranty, in years"
            >
              {WARRANTY_YEARS.map((y) => <option key={y} value={y}>{y}-Year Parts</option>)}
            </select>
            <span className="text-emerald-400">·</span>
            <select
              value={w.labor}
              onChange={(e) => commitWarranty(w.parts, Number(e.target.value))}
              className="h-6 rounded border border-emerald-200 bg-white px-1 text-xs font-bold text-emerald-800 focus:border-emerald-500 focus:outline-none"
              title="Labor warranty, in years"
            >
              {WARRANTY_YEARS.map((y) => <option key={y} value={y}>{y}-Year Labor</option>)}
            </select>
            {(w.source === "unit" || w.source === "text") && (
              <button type="button" disabled={resetting} onClick={() => void resetWarranty()} className="ml-0.5 text-[11px] font-semibold text-slate-500 hover:text-slate-800 disabled:opacity-50" title="Back to this model's standard warranty (from the pricebook), else the tier standard">
                Standard
              </button>
            )}
            <button type="button" onClick={() => setEditWarranty(false)} className="ml-0.5 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Keep as is" aria-label="Close">
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
        {showQty && (
          <QtyStepper
            qty={l.qty}
            onChange={(q) => update.mutate({ id: l.id, qty: q, ...(isInc ? { discount: q * l.unit_price } : {}) })}
          />
        )}
        <span className="ml-auto inline-flex items-center gap-1.5">
          <MoneyField
            className="w-24"
            inputClassName="h-7 text-[13px]"
            value={l.unit_price}
            onCommit={(v) => update.mutate({ id: l.id, unit_price: v, ...(isInc ? { discount: l.qty * v } : {}) })}
            title={isInc ? "The value shown to the homeowner (charged as $0)" : "Override this unit's price for this quote"}
          />
          <span className="w-16 text-right text-[13px] font-bold tabular-nums text-slate-800">
            {isInc ? <span className="text-emerald-700">$0</span> : money(lineTotal(l))}
          </span>
        </span>
      </div>
    </li>
  );
}

/** Custom one-time items — a free-text name + price the tech can add to any tier. */
function CustomItemsBlock({ o }: { o: QuoteOptionFull }) {
  const add = useAddQuoteLine();
  const remove = useRemoveQuoteLine();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const customs = o.lines.filter((l) => l.kind === "custom");
  const submit = () => {
    if (!name.trim()) return;
    add.mutate({ option_id: o.id, kind: "custom", description: name.trim(), unit_price: Number(price) || 0 });
    setName("");
    setPrice("");
    setAdding(false);
  };
  return (
    <div>
      <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-600">
        <Plus className="h-3.5 w-3.5" /> Custom Items
      </div>
      {customs.length > 0 && (
        <ul className="mb-1.5 space-y-1.5">
          {customs.map((l) => (
            <LineRow key={l.id} l={l} option={o} onRemove={() => remove.mutate(l.id)} />
          ))}
        </ul>
      )}
      {adding ? (
        <div className="flex items-center gap-1.5">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="Item name"
            className="min-w-0 flex-1 rounded-md border border-hairline px-2 py-1 text-xs text-slate-700 focus:border-brand-400 focus:outline-none"
          />
          <div className="relative w-20 shrink-0">
            <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="0"
              className="w-full rounded-md border border-hairline py-1 pl-5 pr-1.5 text-right text-xs tabular-nums focus:border-brand-400 focus:outline-none"
            />
          </div>
          <button onClick={submit} className="shrink-0 rounded-md bg-brand-600 px-2 py-1 text-xs font-semibold text-white hover:bg-brand-700">
            Add
          </button>
          <button
            onClick={() => { setAdding(false); setName(""); setPrice(""); }}
            className="shrink-0 text-slate-400 hover:text-slate-700"
            aria-label="Cancel"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-hairline px-3 py-2.5 text-xs font-semibold text-slate-500 transition-colors hover:border-brand-300 hover:bg-brand-50/40 hover:text-brand-700"
        >
          <Plus className="h-3.5 w-3.5" /> Add custom item
        </button>
      )}
    </div>
  );
}

/** Named discounts (% or $), one or more per tier — the proposal-app "custom-named discounts". */
/* ── QUOTE-POLISH-05: itemized utility rebates on a collection ──────────────
   One row per rebate: who pays it (a utility from Settings, or "Other" with a
   name — manufacturer promo, tax credit…) + this job's amount. Shown to the
   homeowner as money BACK (paid by the utility), never subtracted from the
   contract. The option's rebate_total mirrors the rows (DB trigger). ── */
function RebatesBlock({ o, utilities }: { o: QuoteOptionFull; utilities: Utility[] }) {
  const add = useAddQuoteRebate();
  const update = useUpdateQuoteRebate();
  const remove = useRemoveQuoteRebate();
  const rebates = o.rebates ?? [];
  const sel = "h-8 min-w-0 rounded-md border border-hairline bg-white px-2 text-xs text-slate-700 focus:border-brand-400 focus:outline-none";
  // Suggest the next utility the collection doesn't carry yet (gas, then electric).
  const used = new Set(rebates.map((r) => r.utility_id).filter(Boolean));
  const nextUtility = utilities.find((u) => !used.has(u.id)) ?? null;
  return (
    <div className="space-y-1.5">
      {rebates.map((r, i) => {
        const custom = !r.utility_id;
        return (
          <div key={r.id} className="flex items-center gap-1.5">
            <span className="w-[72px] shrink-0 text-xs font-semibold text-slate-600">{i === 0 ? (rebates.length > 1 ? "Rebates" : "Rebate") : ""}</span>
            <select
              value={r.utility_id ?? "custom"}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "custom") update.mutate({ id: r.id, utility_id: null, custom_name: r.custom_name || "Rebate" });
                else update.mutate({ id: r.id, utility_id: v, custom_name: null });
              }}
              className={cn(sel, custom ? "w-[92px] shrink-0" : "flex-1")}
              title="Who pays this rebate"
            >
              {utilities.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
              <option value="custom">Other…</option>
            </select>
            {custom && (
              <TextField
                value={r.custom_name ?? ""}
                onCommit={(v) => update.mutate({ id: r.id, custom_name: v.trim() || "Rebate" })}
                placeholder="Rebate name (e.g. Lennox promo, tax credit)"
                className="min-w-0 flex-1 rounded-md border border-hairline bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-brand-400 focus:outline-none"
              />
            )}
            <MoneyField
              className="w-24 shrink-0"
              inputClassName="h-8"
              value={r.amount || 0}
              onCommit={(v) => update.mutate({ id: r.id, amount: v })}
              title="This job's rebate amount — shown to the customer as money back, never subtracted from the contract"
            />
            <button
              type="button"
              onClick={() => remove.mutate(r.id)}
              title="Remove this rebate"
              className="shrink-0 rounded-md p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-600"
              aria-label="Remove rebate"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
      <div className="flex items-center gap-2">
        {rebates.length === 0 && <span className="w-[72px] shrink-0 text-xs font-semibold text-slate-600">Rebate</span>}
        <button
          type="button"
          disabled={add.isPending}
          onClick={() =>
            add.mutate({
              option_id: o.id,
              utility_id: nextUtility?.id ?? null,
              custom_name: nextUtility ? null : "Rebate",
              amount: nextUtility?.default_rebate ?? 0,
              sort: rebates.length,
            })
          }
          className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 transition-colors hover:text-brand-800 disabled:opacity-50"
          title={nextUtility ? `Add a ${nextUtility.name} rebate` : "Add a Rebate"}
        >
          <Plus className="h-3 w-3" /> {rebates.length === 0 ? "Add a Rebate" : "Add Another Rebate"}
          {rebates.length === 0 && <span className="font-normal text-slate-400">· gas and electric can each have one</span>}
        </button>
      </div>
    </div>
  );
}

function DiscountsBlock({ o }: { o: QuoteOptionFull }) {
  const add = useAddQuoteDiscount();
  const update = useUpdateQuoteDiscount();
  const remove = useRemoveQuoteDiscount();
  const sub = optionSubtotal(o);
  const discounts = o.discounts ?? [];
  return (
    <div className="space-y-1.5">
      {discounts.map((d) => (
        <div key={d.id} className="flex items-center gap-1.5">
          <TextField
            value={d.label ?? ""}
            onCommit={(v) => update.mutate({ id: d.id, label: v })}
            placeholder="Discount name"
            className="min-w-0 flex-1 rounded-md border border-hairline bg-white px-2 py-1 text-xs text-slate-700 focus:border-brand-400 focus:outline-none"
          />
          <div className="inline-flex shrink-0 rounded-md border border-hairline p-0.5">
            <button
              onClick={() => update.mutate({ id: d.id, mode: "amount" })}
              className={cn("rounded px-1.5 py-0.5 text-[11px] font-semibold transition-colors", d.mode === "amount" ? "bg-slate-800 text-white" : "text-slate-400 hover:text-slate-700")}
            >
              $
            </button>
            <button
              onClick={() => update.mutate({ id: d.id, mode: "percent" })}
              className={cn("rounded px-1.5 py-0.5 text-[11px] font-semibold transition-colors", d.mode === "percent" ? "bg-slate-800 text-white" : "text-slate-400 hover:text-slate-700")}
            >
              %
            </button>
          </div>
          <MoneyField
            className="w-16 shrink-0"
            prefix={null}
            value={d.value || 0}
            onCommit={(v) => update.mutate({ id: d.id, value: v })}
            title={d.mode === "percent" ? "Percent off the collection subtotal" : "Dollars off"}
          />
          <span className="w-14 shrink-0 text-right text-xs font-semibold tabular-nums text-rose-600" title="Discount amount">
            −{money(resolveDiscount(d, sub))}
          </span>
          <button onClick={() => remove.mutate(d.id)} className="shrink-0 text-slate-300 hover:text-rose-600" aria-label="Remove discount">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <button
        onClick={() => add.mutate({ option_id: o.id, sort: discounts.length })}
        className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 transition-colors hover:text-brand-800"
      >
        <Plus className="h-3 w-3" /> Add Discount
      </button>
    </div>
  );
}

/* ── QUOTE-POLISH-11: the Favorites ▾ menu (the approved mock) — load a saved
   build (fills every collection, two taps if something's already built) or
   save this one. QUOTE-FAV-02 (Seth): favorites are PERSONAL — this is MY
   list — so the menu is also where I rename, replace (edit) and delete them.
   QUOTE-FAV-03 (Seth): the office can PUSH one to the techs — "Share With
   the Team" puts it in everyone's menu under From the Office (Fill only for
   everyone but its owner). Packages stay the curated team library. ────── */
function FavoritesMenu({ quote, hasContent, onClose, onSave, onLoaded }: { quote: Quote; hasContent: boolean; onClose: () => void; onSave: () => void; onLoaded: (name: string) => void }) {
  const { data, isLoading } = useQuoteTemplates();
  const me = useCurrentUser();
  const apply = useApplyTemplate();
  const delTpl = useDeleteTemplate();
  const rename = useRenameTemplate();
  const replace = useReplaceTemplate();
  const share = useShareTemplate();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [delId, setDelId] = useState<string | null>(null);
  const [replaceId, setReplaceId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [openRow, setOpenRow] = useState<string | null>(null);
  const templates = data ?? [];
  const mine = templates.filter((t) => t.mine);
  const fromOffice = templates.filter((t) => !t.mine);
  const nameOf = (t: QuoteTemplate) => t.template_name ?? t.title ?? "Favorite";
  const pick = (t: QuoteTemplate) => {
    if (hasContent && confirmId !== t.id) { setConfirmId(t.id); return; }
    apply.mutate({ template: t, targetQuoteId: quote.id }, { onSuccess: () => onLoaded(nameOf(t)) });
  };
  const startRename = (t: QuoteTemplate) => { setEditId(t.id); setEditName(nameOf(t)); };
  const commitRename = (t: QuoteTemplate) => {
    const name = editName.trim();
    if (name && name !== nameOf(t)) rename.mutate({ id: t.id, name });
    setEditId(null);
  };
  // A render FUNCTION, not an inner component — an inner component would
  // remount on every keystroke and drop focus out of the rename box.
  const renderRow = (t: QuoteTemplate) => (
    <div key={t.id} className={cn("px-3 py-2 hover:bg-slate-50", openRow === t.id && "bg-slate-50")}>
      <div className="flex items-center gap-2.5">
        <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg text-sm font-extrabold", t.mine ? "bg-amber-50 text-amber-700" : "bg-brand-50 text-brand-700")}>{nameOf(t).trim().charAt(0).toUpperCase()}</span>
        <span className="min-w-0 flex-1">
          {editId === t.id ? (
            <input
              autoFocus
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={() => commitRename(t)}
              onKeyDown={(e) => { if (e.key === "Enter") commitRename(t); if (e.key === "Escape") setEditId(null); }}
              className="w-full rounded-md border border-brand-300 px-1.5 py-0.5 text-xs font-semibold text-slate-800 focus:outline-none"
              aria-label="Favorite name"
            />
          ) : (
            <span className="flex items-center gap-1.5 truncate text-xs font-semibold text-slate-800">
              <span className="truncate">{nameOf(t)}</span>
              {t.mine && t.template_shared && <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-brand-50 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-brand-700"><Users className="h-2.5 w-2.5" /> Shared</span>}
            </span>
          )}
          <span className="block truncate text-[11px] text-slate-400">
            {!t.mine && t.template_shared_by ? <>{t.template_shared_by} · </> : null}
            {t.quote_type === "service" ? "Repair" : "System"} · {t.offering_mode === "single" ? "single offering" : "Good / Better / Best"}
            {(t.template_use_count ?? 0) > 0 ? <> · used {t.template_use_count}×</> : null}
            {" · saved "}{new Date(t.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </span>
        </span>
        <button
          type="button"
          disabled={apply.isPending}
          onClick={() => pick(t)}
          className={cn("shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors", confirmId === t.id ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-brand-50 text-brand-700 hover:bg-brand-100")}
        >
          {apply.isPending && confirmId === t.id ? "Filling…" : confirmId === t.id ? "Replace what's built?" : "Fill"}
        </button>
        {t.mine ? (
          <button
            type="button"
            onClick={() => { setOpenRow((v) => (v === t.id ? null : t.id)); setDelId(null); setReplaceId(null); }}
            className={cn("shrink-0 rounded p-1 transition-colors", openRow === t.id ? "bg-slate-200 text-slate-700" : "text-slate-300 hover:text-slate-600")}
            aria-label="More actions for this favorite"
            aria-expanded={openRow === t.id}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        ) : (
          <span className="w-6 shrink-0" aria-hidden="true" />
        )}
      </div>
      {t.mine && openRow === t.id && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-[2.6rem]">
          <button type="button" onClick={() => startRename(t)} className="inline-flex items-center gap-1 rounded-full border border-hairline bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-100">
            <Pencil className="h-3 w-3" /> Rename
          </button>
          <button
            type="button"
            disabled={share.isPending}
            title={t.template_shared ? "Take it out of everyone else's Favorites menu" : "Put it in every tech's Favorites menu, under From the Office"}
            onClick={() => share.mutate({ id: t.id, shared: !t.template_shared, byName: me.data?.firstName ?? null })}
            className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors", t.template_shared ? "border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100" : "border-hairline bg-white text-slate-600 hover:bg-slate-100")}
          >
            <Users className="h-3 w-3" /> {share.isPending ? "Saving…" : t.template_shared ? "Stop Sharing" : "Share With the Team"}
          </button>
          <button
            type="button"
            disabled={!hasContent || replace.isPending}
            title={!hasContent ? "Build something first — this swaps the favorite's collections for what's on this quote" : "Swap this favorite's collections for what's built on this quote (name stays)"}
            onClick={() => { if (replaceId === t.id) { replace.mutate({ template: t, quote }, { onSuccess: () => { setReplaceId(null); setOpenRow(null); } }); } else setReplaceId(t.id); }}
            className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:opacity-40", replaceId === t.id ? "border-amber-300 bg-amber-500 text-white hover:bg-amber-600" : "border-hairline bg-white text-slate-600 hover:bg-slate-100")}
          >
            <RefreshCw className="h-3 w-3" /> {replace.isPending && replaceId === t.id ? "Replacing…" : replaceId === t.id ? "Overwrite it with this build?" : "Replace With This Build"}
          </button>
          <button
            type="button"
            onClick={() => { if (delId === t.id) { delTpl.mutate(t.id); setDelId(null); setOpenRow(null); } else setDelId(t.id); }}
            className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors", delId === t.id ? "border-red-200 bg-red-600 text-white hover:bg-red-700" : "border-hairline bg-white text-slate-600 hover:border-red-200 hover:text-red-600")}
          >
            <Trash2 className="h-3 w-3" /> {delId === t.id ? "Really delete?" : "Delete"}
          </button>
        </div>
      )}
    </div>
  );
  return (
    <>
      <div className="fixed inset-0 z-20" onClick={onClose} />
      {/* Anchored LEFT below lg (the button sits at the left of a wrapped action row on a tablet — right-anchored, the menu ran off the screen edge); right-anchored on desktop where the cluster hugs the right. */}
      <div role="menu" data-glass="panel" className="absolute left-0 z-30 mt-1 w-[26rem] max-w-[92vw] overflow-hidden rounded-xl py-1 lg:left-auto lg:right-0">
        <div className="flex items-baseline justify-between px-3 pb-1 pt-2">
          <span className="text-[10px] font-extrabold uppercase tracking-[0.08em] text-slate-400">My Favorites</span>
          <span className="text-[10px] font-medium text-slate-400">Only you see these</span>
        </div>
        <div className="max-h-[50vh] overflow-y-auto">
          {isLoading ? (
            <div className="mx-3 my-2 h-12 animate-pulse rounded-lg bg-slate-50" />
          ) : mine.length === 0 ? (
            <p className="px-3 py-2 text-xs text-slate-500">No favorites yet — build a proposal you&apos;d reuse, then <b>Save This Build as a Favorite</b> below. Favorites are yours alone; they save you the re-typing.</p>
          ) : (
            mine.map(renderRow)
          )}
          {fromOffice.length > 0 && (
            <>
              <div className="mt-1 flex items-baseline justify-between border-t border-hairline/70 px-3 pb-1 pt-2">
                <span className="text-[10px] font-extrabold uppercase tracking-[0.08em] text-brand-700">From the Office</span>
                <span className="text-[10px] font-medium text-slate-400">Shared with the whole team</span>
              </div>
              {fromOffice.map(renderRow)}
            </>
          )}
        </div>
        <div className="my-1 border-t border-hairline/70" />
        <button
          type="button"
          role="menuitem"
          disabled={!hasContent}
          onClick={onSave}
          className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Star className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <span>
            <span className="block text-xs font-semibold text-brand-700">Save This Build as a Favorite…</span>
            <span className="block text-[11px] leading-snug text-slate-400">{hasContent ? "Collections, lines, rebates, discounts, the add-ons shelf — never the customer or notes" : "Build at least one collection first"}</span>
          </span>
        </button>
        <p className="px-3 pb-2 pt-1 text-[11px] leading-snug text-slate-400">
          Favorites are your own shortcuts; <b className="font-semibold text-slate-500">Share With the Team</b> pushes one to every tech&apos;s menu. The curated library is <Link to="/settings/quotes" onClick={onClose} className="font-semibold text-slate-500 hover:text-brand-700">Packages in Settings → Quotes</Link>.
        </p>
      </div>
    </>
  );
}

/** Name it — or, if the name matches one of MY favorites, update that one.
 *  QUOTE-FAV-03: a "share with the team" switch, for the office pushing a set. */
function SaveFavoriteDialog({ quote, busy, onClose, onSave, onReplace }: { quote: Quote; busy: boolean; onClose: () => void; onSave: (name: string, shared: boolean, byName: string | null) => void; onReplace: (t: QuoteTemplate) => void }) {
  const [name, setName] = useState(quote.title ?? "");
  const [shared, setShared] = useState(false);
  const { data } = useQuoteTemplates();
  const me = useCurrentUser();
  const mine = (data ?? []).filter((t) => t.mine);
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  const ok = name.trim().length > 1;
  const existing = mine.find((t) => (t.template_name ?? t.title ?? "").trim().toLowerCase() === name.trim().toLowerCase()) ?? null;
  const go = () => { if (!ok || busy) return; if (existing) onReplace(existing); else onSave(name.trim(), shared, me.data?.firstName ?? null); };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-24" onClick={onClose}>
      <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-e4" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 pb-2 pt-4">
          <div className="text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-amber-600">My Favorites</div>
          <h3 className="text-xl font-bold tracking-tight text-slate-900">Save This Build as a Favorite</h3>
        </div>
        <div className="space-y-3 px-5 pb-5">
          <label className="block text-xs font-semibold text-slate-500">
            Name — how it reads in the Favorites menu
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") go(); }}
              placeholder="e.g. Lennox HE Set — SLP99 / EL297 / ML193"
              className="mt-1 w-full rounded-xl border border-hairline px-3 py-2 text-sm font-semibold text-slate-800 focus:border-brand-400 focus:outline-none"
            />
          </label>
          {mine.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] font-medium text-slate-400">Or update one of yours:</span>
              {mine.map((t) => (
                <button key={t.id} type="button" onClick={() => setName(t.template_name ?? t.title ?? "")} className={cn("rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition-colors", existing?.id === t.id ? "border-amber-300 bg-amber-50 text-amber-800" : "border-hairline bg-white text-slate-600 hover:bg-slate-50")}>
                  {t.template_name ?? t.title}
                </button>
              ))}
            </div>
          )}
          {!existing && (
            <label className={cn("flex cursor-pointer items-start gap-2.5 rounded-xl border px-3 py-2.5 transition-colors", shared ? "border-brand-200 bg-brand-50/70" : "border-hairline bg-white hover:bg-slate-50")}>
              <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} className="mt-0.5 h-4 w-4 accent-brand-600" />
              <span>
                <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-800"><Users className="h-3.5 w-3.5 text-brand-600" /> Share with the whole team</span>
                <span className="block text-[11px] leading-snug text-slate-500">Shows in every tech&apos;s Favorites menu under <b>From the Office</b>. They can fill from it; only you can change or delete it.</span>
              </span>
            </label>
          )}
          <div className={cn("rounded-xl border px-3 py-2 text-[12px] leading-relaxed text-slate-600", existing ? "border-amber-200 bg-amber-50/70" : "border-brand-100 bg-brand-50/60")}>
            {existing ? (
              <>You already have a favorite called <b>{existing.template_name ?? existing.title}</b> — saving <b>replaces its collections</b> with this build. Its name{existing.template_shared ? " and sharing" : ""} stay.</>
            ) : (
              <>Saves the whole build: every collection&apos;s equipment, accessories with their Included / Required settings, rebates, discounts and the Recommended Add-Ons shelf. Leaves out the customer, hand-typed notes and the dispatch.{shared ? "" : " Only you see it."}</>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-full border border-hairline bg-white px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50">Cancel</button>
            <button type="button" disabled={!ok || busy} onClick={go} className={cn("rounded-full px-4 py-2 text-xs font-bold text-white disabled:opacity-50", existing ? "bg-amber-600 hover:bg-amber-700" : "bg-brand-600 hover:bg-brand-700")}>
              {busy ? "Saving…" : existing ? `Replace “${(existing.template_name ?? existing.title ?? "").slice(0, 28)}”` : shared ? "Save & Share" : "Save Favorite"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── GBB-GALLERY-01: his "Recommended Add-ons" — the accessory catalog as
   tap-to-toggle tiles, grouped by the office's dropdown cards. Pick the
   collection they land on; a tile lights up when that collection has it,
   and tapping again takes it back off. ─────────────────────────────────── */
function AddOnGallery({ quote, options, allCats }: { quote: Quote; options: QuoteOptionFull[]; allCats: QuoteCategory[] }) {
  const accParent = allCats.find((c) => c.is_active && !c.parent_key && (c.kind === "accessory" || c.kind === "enhancement") && c.pricebook_category);
  const kids = allCats.filter((c) => c.parent_key === accParent?.key && c.is_active).sort((a, b) => a.sort - b.sort);
  const { data: items } = useEquipmentItems(accParent?.pricebook_category ?? null, "");
  const add = useAddQuoteLine();
  const remove = useRemoveQuoteLine();
  const update = useUpdateQuoteLine();
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  void quote;
  if (!accParent || options.length === 0) return null;

  const all = items ?? [];
  const groups: { label: string; items: Item[] }[] = [];
  const seen = new Set<string>();
  for (const k of kids) {
    const inGroup = all.filter((it) => it.subcategory === k.pricebook_subcategory);
    if (inGroup.length) { groups.push({ label: k.label, items: inGroup }); inGroup.forEach((it) => seen.add(it.id)); }
  }
  const rest = all.filter((it) => !seen.has(it.id));
  if (rest.length) groups.push({ label: "More", items: rest });

  // QUOTE-POLISH-10 (Seth: "the recommended add-ons go on the homeowner page as
  // suggested items the homeowner can click to add to their package after the
  // fact … only the homeowner can choose that"): an offered add-on is ONE
  // suggestion that rides on EVERY collection (a recommended line per
  // collection, matched by item), never inside a package. The homeowner's tap
  // on the proposal page is what moves it into whichever package they choose.
  const keyOf = (l: QuoteLine) => l.item_id ?? `d:${(l.description ?? "").trim().toLowerCase()}`;
  const offered = (() => {
    const m = new Map<string, { key: string; line: QuoteLine; lines: QuoteLine[] }>();
    for (const o of options) for (const l of o.lines) {
      if (l.role !== "recommended") continue;
      const k = keyOf(l);
      const cur = m.get(k) ?? { key: k, line: l, lines: [] };
      cur.lines.push(l);
      m.set(k, cur);
    }
    return [...m.values()];
  })();
  const offeredFor = (it: Item) => offered.find((a) => a.key === it.id);
  const offerAll = async (it: Item) => {
    setBusy(it.id);
    try {
      for (const o of options) {
        if (o.lines.some((l) => l.role === "recommended" && l.item_id === it.id)) continue;
        await add.mutateAsync({ option_id: o.id, kind: accParent.key, item: it, role: "recommended" });
      }
    } finally { setBusy(null); }
  };
  const withdrawAll = async (a: { key: string; lines: QuoteLine[] }) => {
    setBusy(a.key);
    try { for (const l of a.lines) await remove.mutateAsync(l.id); } finally { setBusy(null); }
  };
  const repriceAll = async (a: { key: string; lines: QuoteLine[] }, price: number) => {
    setBusy(a.key);
    try { for (const l of a.lines) await update.mutateAsync({ id: l.id, unit_price: price, discount: 0 }); } finally { setBusy(null); }
  };
  const toggle = (it: Item) => {
    const a = offeredFor(it);
    if (a) void withdrawAll(a); else void offerAll(it);
  };

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 text-sm font-bold text-slate-900">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <Star className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-12")} />
          </span>
          Recommended Add-Ons
          {offered.length > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800">{offered.length} offered</span>}
        </button>
        <span className="text-xs font-medium text-slate-400">· below the packages on the proposal · homeowner adds</span>
      </div>

      {offered.length > 0 && (
        <ul className="mt-3 grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {offered.map((a) => (
            <li key={a.key} className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/50 px-2.5 py-2">
              {a.line.image_url ? <img src={a.line.image_url} alt="" className="h-9 w-9 shrink-0 rounded object-contain" /> : <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-white text-[9px] font-bold text-slate-400">HV</span>}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12.5px] font-semibold text-slate-800">{a.line.description ?? a.line.model ?? "Item"}</span>
                <span className="block truncate text-[10.5px] text-amber-700">Offered on every collection · the homeowner decides</span>
              </span>
              <MoneyField
                className="w-20 shrink-0"
                inputClassName="h-7 text-[12.5px]"
                value={a.line.unit_price}
                onCommit={(v) => void repriceAll(a, v)}
                title="What they'd pay to add it — same on every collection"
              />
              <button type="button" disabled={busy === a.key} onClick={() => void withdrawAll(a)} title="Stop offering this add-on" className="shrink-0 rounded-md p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50">
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {open && groups.map((g) => (
        <div key={g.label} className="mt-3">
          <div className="mb-1.5 text-center text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-slate-400">{g.label}</div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {g.items.map((it) => {
              const on = !!offeredFor(it);
              return (
                <button
                  key={it.id}
                  type="button"
                  disabled={busy === it.id}
                  onClick={() => toggle(it)}
                  title={on ? "Offered — click to withdraw the suggestion" : "Offer this as a Recommended Add-On (the homeowner decides)"}
                  className={cn(
                    "flex items-center gap-2.5 rounded-xl border px-2.5 py-2 text-left transition-all disabled:opacity-60",
                    on ? "border-amber-300 bg-amber-50/70 ring-1 ring-amber-200" : "border-hairline bg-white hover:-translate-y-px hover:shadow-e1",
                  )}
                >
                  {it.image_url ? (
                    <img src={it.image_url} alt="" className="h-9 w-9 shrink-0 rounded object-contain" />
                  ) : (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-slate-100 text-[9px] font-bold text-slate-400">HV</span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] font-semibold text-slate-800">{it.description ?? it.part_number}</span>
                    <span className="block truncate text-[10.5px] text-slate-400">{it.model ?? ""}</span>
                  </span>
                  <span className={cn("shrink-0 text-[13px] font-bold tabular-nums", on ? "text-amber-800" : "text-slate-600")}>
                    {(it.sell_price ?? it.base_price) != null ? `+${money(Number(it.sell_price ?? it.base_price))}` : "—"}
                  </span>
                  {on && <CheckCircle2 className="h-4 w-4 shrink-0 text-amber-600" />}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </Card>
  );
}

/** GBB-PICKER-01 (Seth 2026-09-30, from his proposal app's "Select Equipment"
 *  popup): the list shows IMMEDIATELY, ranked — this collection's tier first,
 *  in the office's Package Ordering, so the right unit is usually the top
 *  row. Model number reads like a model number, the tier rides as a chip,
 *  and search still cuts through everything. */
const PICKER_TIER_CHIP: Record<string, string> = {
  best: "bg-gradient-to-b from-amber-200 to-amber-300 text-amber-900",
  better: "bg-sky-100 text-sky-800",
  good: "bg-slate-100 text-slate-600",
};
function EquipmentPicker({ kind, optionId, pricebookCategory, label, isEquipment, brand, subcategory, category, optionTier, inc, sizing, onClose }: { kind: string; optionId: string; pricebookCategory: string | null; label: string; isEquipment: boolean; brand: string | null; subcategory: string | null; category: string | null; optionTier: QuoteTier | null; inc?: boolean; sizing?: SizingSuggestion | null; onClose: () => void }) {
  const [search, setSearch] = useState("");
  // Equipment is brand-scoped to the collection (Lennox shows only Lennox); accessories are brand-agnostic + category-scoped.
  const filterBrand = isEquipment ? brand : null;
  const { data: items, isLoading } = useEquipmentItems(pricebookCategory, search, filterBrand, subcategory, category);
  const add = useAddQuoteLine();
  // SIZE-ASSIST-01: which size family this slot prices (furnace→heating,
  // AC/HP/coil→cooling) — drives the Right Size badge + sort-first. Nothing
  // is ever hidden; the right-size gear just surfaces first.
  const sizeHint = isEquipment ? (familyHint(label) ?? familyHint(pricebookCategory) ?? familyHint(kind)) : null;
  const rightSized = (it: Item) => isRightSize(it.model ?? it.part_number, sizing ?? null, sizeHint);
  const sizeLabel = sizeHint === "heating"
    ? (sizing?.heatInputKbtu ? `~${sizing.heatInputKbtu}K BTU` : "")
    : (sizing?.coolingTons ? `~${sizing.coolingTons} ton` : "");
  const sizeGroups = isEquipment && !!sizing && !!sizeHint && !!sizeLabel;

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const pick = (it: Item) => {
    add.mutate({ option_id: optionId, kind, item: it, role: "included", inc: !!inc && !isEquipment });
    onClose();
  };

  // QUOTE-POLISH-01: a typed search reaches the WHOLE pricebook — this slot's
  // own items first, the rest of the catalog after a divider (the empty-state
  // always promised "type to search everything"; now it's true).
  const typedSearch = !!search.trim();
  const inSlot = (it: Item) => !subcategory || it.subcategory === subcategory;
  // Right-size matches float above everything, then this collection's tier,
  // then the office's rank breaks ties.
  const TIER_RANK: Record<string, number> = { best: 0, better: 1, good: 2 };
  const ranked = [...(items ?? [])].sort((a, b) => {
    if (subcategory && typedSearch) {
      const ai = inSlot(a) ? 0 : 1, bi = inSlot(b) ? 0 : 1;
      if (ai !== bi) return ai - bi;
    }
    const as = rightSized(a) ? 0 : 1, bs = rightSized(b) ? 0 : 1;
    if (as !== bs) return as - bs;
    if (optionTier) {
      const am = a.tier === optionTier ? 0 : a.tier ? 1 : 2;
      const bm = b.tier === optionTier ? 0 : b.tier ? 1 : 2;
      if (am !== bm) return am - bm;
      if (am === 1 && a.tier !== b.tier) return (TIER_RANK[a.tier!] ?? 9) - (TIER_RANK[b.tier!] ?? 9);
    }
    const ao = a.gbb_order ?? 9e9, bo = b.gbb_order ?? 9e9;
    if (ao !== bo) return ao - bo;
    return Number(a.sell_price ?? a.base_price ?? 0) - Number(b.sell_price ?? b.base_price ?? 0);
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-16" onClick={onClose}>
      <div className="w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-e4" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 pb-3 pt-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-brand-600">Select {isEquipment ? "Equipment" : "Item"}</div>
              <h3 className="text-xl font-bold tracking-tight text-slate-900">{label}</h3>
            </div>
            <button onClick={onClose} aria-label="Close" className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2 rounded-full border border-hairline bg-slate-50/70 px-3.5 py-2">
            <Search className="h-4 w-4 shrink-0 text-slate-400" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, model, or description…"
              className="flex-1 bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400"
            />
          </div>
        </div>
        <div className="max-h-[56vh] space-y-2 overflow-y-auto px-4 pb-4">
          {isLoading ? (
            <div className="p-4 text-sm text-slate-400">Loading the catalog…</div>
          ) : ranked.length === 0 ? (
            <div className="p-4 text-sm text-slate-400">
              {search.trim() ? "No matches in the pricebook." : "Nothing in the pricebook for this slot yet — add items under Pricebook, or type to search everything."}
            </div>
          ) : (
            ranked.slice(0, 60).map((it, i, arr) => {
              // QUOTE-POLISH-02: never lead with an internal import key. The
              // proposal-app accessory import keyed its items PA-001…057 —
              // those are database keys, not model numbers. Big line = the
              // real model; else the item's NAME for accessories (the gallery
              // already leads with it); a real part # only for equipment/parts.
              const code = it.model ?? (isSyntheticPart(it.part_number) ? null : it.part_number);
              const primary = code ?? it.description ?? it.part_number ?? "";
              const primaryIsName = !code && !!it.description;
              // QUOTE-POLISH-03 (Seth: "I would love for the recommended sizing to
              // populate accordingly in the lists"): the right-size gear is its own
              // group at the top, named with the home's number; the rest follows.
              const sizedHeader = sizeGroups && i === 0 && rightSized(it);
              const otherHeader = sizeGroups && !rightSized(it) && (i === 0 || rightSized(arr[i - 1]));
              return (
              <Fragment key={it.id}>
              {sizedHeader && (
                <div className="flex items-center gap-2 pb-1 pt-1">
                  <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wide text-emerald-800">Sized for this home</span>
                  <span className="text-xs font-semibold text-emerald-700">{sizeLabel}</span>
                </div>
              )}
              {otherHeader && (
                <div className="flex items-center gap-2 pb-1 pt-3">
                  <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-slate-400">{i === 0 ? `No exact match for ${sizeLabel} — all sizes` : "Other sizes"}</span>
                </div>
              )}
              {subcategory && typedSearch && !inSlot(it) && (i === 0 || inSlot(arr[i - 1])) && (
                <div className="pb-0.5 pt-2 text-center text-[10px] font-extrabold uppercase tracking-[0.14em] text-slate-300">
                  More from the pricebook — other categories
                </div>
              )}
              <button
                onClick={() => pick(it)}
                className="flex w-full items-center gap-3.5 rounded-2xl border border-hairline bg-white px-3.5 py-2.5 text-left shadow-e1 transition-all hover:-translate-y-px hover:border-brand-200 hover:shadow-e2"
              >
                {it.image_url ? (
                  <img src={it.image_url} alt="" className="h-12 w-12 shrink-0 rounded-lg object-contain" />
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[10px] font-bold text-slate-400">HV</div>
                )}
                <div className="min-w-0 flex-1">
                  <div className={cn("truncate text-[13px] font-bold tracking-tight text-slate-900", !primaryIsName && "font-mono")}>
                    {primary}
                  </div>
                  <div className="truncate text-xs font-medium text-brand-700">{primaryIsName ? (it.manufacturer ?? "") : (it.description ?? it.manufacturer ?? "")}</div>
                  <div className="mt-0.5 flex items-center gap-1.5">
                    {rightSized(it) && (
                      <span
                        className="rounded-full bg-emerald-100 px-1.5 py-px text-[9px] font-extrabold uppercase text-emerald-800"
                        title={`Matches the Sizing Assist suggestion${sizing?.confidence === "rough" ? " (rough estimate — confirm with the load calc)" : ""}`}
                      >
                        Right size
                      </span>
                    )}
                    {it.tier && (
                      <span className={cn("rounded-full px-1.5 py-px text-[9px] font-extrabold uppercase", PICKER_TIER_CHIP[it.tier] ?? PICKER_TIER_CHIP.good)}>{it.tier}</span>
                    )}
                    {it.efficiency && <span className="truncate text-[10.5px] text-slate-400">{it.efficiency}</span>}
                  </div>
                </div>
                <span className="shrink-0 text-[15px] font-extrabold tabular-nums text-brand-700">
                  {(it.sell_price ?? it.base_price) != null ? money(Number(it.sell_price ?? it.base_price)) : "—"}
                </span>
              </button>
              </Fragment>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// REPAIR-BUILDER-01 (Seth 2026-08-26): "It should be completely different
// since they are choosing parts." The service quote is a repair TICKET —
// one column, parts-first, phone-sized targets: search a part, tap +/− for
// qty, price right there, labor + diagnostic fee one tap away. The customer
// sees one clean total, not a Good/Better/Best wall.
// ═══════════════════════════════════════════════════════════════════════════

interface RepairItemHit {
  id: string; part_number: string | null; description: string | null; model: string | null;
  manufacturer: string | null; base_price: number | null; average_cost: number | null;
  last_purchase: number | null; qty_in_stock: number | null;
}
/** The accepted-repair CTA: one tap books the return visit into the Board's
 *  queue (Return Visits lane) with the approved price + parts in the notes. */
function BookRepairCta({ quote, option }: { quote: Quote; option: QuoteOptionFull }) {
  const book = useBookRepairVisit();
  const [booked, setBooked] = useState(false);
  if (booked) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold text-white">
          <Check className="h-3.5 w-3.5" /> Repair visit queued
        </span>
        <Link to="/board" className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-3 py-1 text-xs font-bold text-brand-700 transition-colors hover:bg-brand-100">
          Schedule it on the Board <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </span>
    );
  }
  return (
    <button
      type="button"
      disabled={book.isPending || !quote.custno}
      title="Creates a Return Visit ticket on the Board with the approved price + parts — the dispatcher schedules it from there"
      onClick={() => book.mutate({ quote, option }, { onSuccess: () => setBooked(true), onError: (e) => alert((e as Error).message || "Couldn't book the visit.") })}
      className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-60"
    >
      <CalendarClock className="h-3.5 w-3.5" /> {book.isPending ? "Booking…" : "Book the Repair Visit"}
    </button>
  );
}

function RepairBuilder({ quote, options, allCats }: { quote: Quote; options: QuoteOptionFull[]; allCats: QuoteCategory[] }) {
  // BUILDER-FLOW-01 (the mock): a second option is a tab, not a second column — the card keeps its width.
  const sorted = [...options].sort((a, b) => a.sort - b.sort);
  const [activeId, setActiveId] = useState<string | null>(null);
  const active = sorted.find((o) => o.id === activeId) ?? sorted[0] ?? null;
  if (!active) return null;
  return (
    <div className="max-w-3xl">
      {sorted.length > 1 && (
        <div role="tablist" aria-label="Repair options" className="mb-3 inline-flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1">
          {sorted.map((o) => {
            const on = o.id === active.id;
            return (
              <button key={o.id} type="button" role="tab" aria-selected={on} onClick={() => setActiveId(o.id)}
                className={cn("rounded-lg px-3 py-1.5 text-xs font-bold transition-colors", on ? "bg-white text-slate-900 shadow-e1" : "text-slate-500 hover:text-slate-800")}>
                {o.label || o.tier}<span className={cn("ml-1.5 tabular-nums", on ? "text-slate-500" : "text-slate-400")}>{o.lines.length ? money(optionTotal(o)) : "—"}</span>
              </button>
            );
          })}
        </div>
      )}
      <RepairOptionCard key={active.id} quote={quote} o={active} allCats={allCats} showLabel={sorted.length > 1} />
    </div>
  );
}

function RepairOptionCard({ quote, o, allCats, showLabel }: { quote: Quote; o: QuoteOptionFull; allCats: QuoteCategory[]; showLabel: boolean }) {
  const addLine = useAddQuoteLine();
  const updLine = useUpdateQuoteLine();
  const rmLine = useRemoveQuoteLine();
  const updateOption = useUpdateQuoteOption();
  const rmOption = useRemoveQuoteOption();
  const fees = useDiagnosticFees();
  const [feeOpen, setFeeOpen] = useState(false);
  const [manualDesc, setManualDesc] = useState("");
  const [manualAmt, setManualAmt] = useState("");
  const total = optionTotal(o);
  const sorted = [...o.lines].sort((a, b) => a.sort - b.sort || a.id.localeCompare(b.id));
  const nextSort = sorted.length ? Math.max(...sorted.map((l) => l.sort)) + 1 : 0;
  // QUOTE-POLISH-01: new equipment does NOT belong on a repair ticket. Lines
  // whose kind is an equipment slot only exist here when an Install build was
  // flipped to Service — flag them loudly with a one-tap cleanup.
  const equipKeys = new Set(allCats.filter((c) => c.kind === "equipment").map((c) => c.key));
  const equipLines = sorted.filter((l) => equipKeys.has(l.kind));

  const addPart = (hit: RepairItemHit) => {
    addLine.mutate({
      option_id: o.id,
      kind: "custom",
      item: hit as unknown as Item,
      description: hit.description ?? hit.part_number ?? hit.model ?? "Part",
      // REPAIR-PRICE-COST: a customer price or nothing — never what Sedgwick paid.
      unit_price: sellPriceOf(hit as unknown as Item) ?? 0,
      sort: nextSort,
    });
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-hairline bg-white shadow-sm">
      {/* Option head — label (editable when there's more than one) + running total */}
      <div className="flex items-center justify-between gap-3 border-b border-hairline bg-slate-50/70 px-4 py-3">
        {showLabel ? (
          <span className="flex items-center gap-2">
            <input
              key={`${o.id}:${o.label ?? ""}`}
              defaultValue={o.label ?? ""}
              placeholder="Option name (e.g. Repair Only)"
              onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== (o.label ?? "")) updateOption.mutate({ id: o.id, label: v }); }}
              className="w-52 rounded-lg border border-transparent bg-transparent px-2 py-1 text-[15px] font-bold text-slate-800 hover:border-hairline focus:border-brand-400 focus:bg-white focus:outline-none"
            />
            <button type="button" title="Remove this option" onClick={() => { if (confirm("Remove this repair option and its lines?")) rmOption.mutate(o.id); }}
              className="rounded p-1 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-500">
              <Trash2 className="h-4 w-4" />
            </button>
          </span>
        ) : (
          <span className="flex items-center gap-2 text-[15px] font-bold text-slate-800">
            <Wrench className="h-4 w-4 text-brand-600" /> The Repair
          </span>
        )}
        <span className="text-xl font-bold tabular-nums text-slate-900">{money(total)}</span>
      </div>

      <div className="space-y-1 px-4 py-3">
        {equipLines.length > 0 && (
          <div className="mb-2 flex flex-wrap items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-[12.5px] font-medium text-amber-800 ring-1 ring-amber-200">
            <span className="min-w-0 flex-1">
              <b>New equipment on a repair ticket</b> — {equipLines.length} line{equipLines.length === 1 ? "" : "s"} came from the
              Install side of this quote. Service is repair-only; remove them, or flip Type back to Install.
            </span>
            <button
              type="button"
              disabled={rmLine.isPending}
              onClick={() => equipLines.forEach((l) => rmLine.mutate(l.id))}
              className="shrink-0 rounded-full bg-amber-600 px-2.5 py-1 text-[11px] font-bold text-white transition-colors hover:bg-amber-700"
            >
              Remove equipment lines
            </button>
          </div>
        )}
        {sorted.length === 0 && (
          <p className="rounded-xl border border-dashed border-hairline py-6 text-center text-sm text-slate-400">
            Search a part below to start the ticket.
          </p>
        )}
        {sorted.map((l) => {
          const qty = l.qty || 1;
          return (
            <div key={l.id} className={cn("flex items-center gap-2.5 rounded-xl px-1 py-2", equipKeys.has(l.kind) && "bg-amber-50/60 ring-1 ring-amber-200")}>
              <span className="min-w-0 flex-1">
                <input
                  key={`${l.id}:${l.description ?? ""}`}
                  defaultValue={l.description ?? ""}
                  onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== (l.description ?? "")) updLine.mutate({ id: l.id, description: v }); }}
                  className="w-full rounded-lg border border-transparent bg-transparent px-2 py-1 text-[15px] font-semibold text-slate-800 hover:border-hairline focus:border-brand-400 focus:bg-white focus:outline-none"
                />
                {l.model && l.model !== l.description && (
                  <span className="ml-2 block truncate px-2 text-xs text-slate-400">{l.model}</span>
                )}
              </span>
              {/* qty stepper — thumb-sized */}
              <span className="flex shrink-0 items-center rounded-lg border border-hairline">
                <button type="button" disabled={updLine.isPending || qty <= 1} onClick={() => updLine.mutate({ id: l.id, qty: qty - 1 })}
                  className="flex h-9 w-9 items-center justify-center text-slate-500 transition-colors hover:bg-slate-50 disabled:opacity-30">
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-8 text-center text-[15px] font-bold tabular-nums text-slate-800">{qty}</span>
                <button type="button" disabled={updLine.isPending} onClick={() => updLine.mutate({ id: l.id, qty: qty + 1 })}
                  className="flex h-9 w-9 items-center justify-center text-slate-500 transition-colors hover:bg-slate-50">
                  <Plus className="h-4 w-4" />
                </button>
              </span>
              <span className="relative shrink-0">
                <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                <input
                  key={`${l.id}:p:${l.unit_price}`}
                  type="number"
                  defaultValue={l.unit_price || ""}
                  placeholder="0"
                  onBlur={(e) => { const v = e.target.value === "" ? 0 : Number(e.target.value); if (v !== l.unit_price) updLine.mutate({ id: l.id, unit_price: v }); }}
                  className="h-9 w-24 rounded-lg border border-hairline pl-6 pr-2 text-right text-[15px] tabular-nums focus:border-brand-400 focus:outline-none"
                />
              </span>
              <span className="w-20 shrink-0 text-right text-[15px] font-bold tabular-nums text-slate-700">{money(lineTotal(l))}</span>
              <button type="button" title="Remove line" onClick={() => rmLine.mutate(l.id)}
                className="shrink-0 rounded p-1.5 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-500">
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}

        {/* ADD A PART — the star of the show */}
        <RepairPartSearch onPick={addPart} />

        {/* Quick adds: labor, diagnostic fee, manual line */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button type="button" disabled={addLine.isPending}
            onClick={() => addLine.mutate({ option_id: o.id, kind: "custom", description: "Labor", unit_price: 0, sort: nextSort })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-[13px] font-semibold text-slate-700 transition-colors hover:bg-slate-200">
            <Clock className="h-4 w-4" /> Add Labor
          </button>
          <span className="relative">
            <button type="button" onClick={() => setFeeOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-[13px] font-semibold text-slate-700 transition-colors hover:bg-slate-200">
              <Stethoscope className="h-4 w-4" /> Add Diagnostic Fee
            </button>
            {feeOpen && (
              <>
                <button type="button" className="fixed inset-0 z-10 cursor-default" aria-label="Close" onClick={() => setFeeOpen(false)} tabIndex={-1} />
                <span className="absolute left-0 top-11 z-20 block w-72 overflow-hidden rounded-xl border border-hairline bg-white py-1 shadow-lg">
                  {(fees.data ?? []).map((f) => (
                    <button key={f.id} type="button"
                      onClick={() => { setFeeOpen(false); addLine.mutate({ option_id: o.id, kind: "custom", description: f.label, unit_price: f.amount, sort: nextSort }); }}
                      className="flex w-full items-center justify-between gap-2 px-3.5 py-2.5 text-left text-[13px] font-medium text-slate-700 transition-colors hover:bg-slate-50">
                      <span className="min-w-0 truncate">{f.label}</span>
                      <span className="shrink-0 font-bold tabular-nums">{money(f.amount)}</span>
                    </button>
                  ))}
                  {(fees.data ?? []).length === 0 && <span className="block px-3.5 py-2.5 text-[13px] text-slate-400">No diagnostic fees set up.</span>}
                </span>
              </>
            )}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <input value={manualDesc} onChange={(e) => setManualDesc(e.target.value)} placeholder="Or type a line (e.g. Flush drain line)"
            className="h-10 min-w-[11rem] flex-1 rounded-lg border border-hairline px-3 text-sm focus:border-brand-400 focus:outline-none" />
          <span className="relative w-28">
            <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
            <input type="number" value={manualAmt} onChange={(e) => setManualAmt(e.target.value)} placeholder="price"
              className="h-10 w-full rounded-lg border border-hairline pl-6 pr-2.5 text-right text-sm tabular-nums focus:border-brand-400 focus:outline-none" />
          </span>
          <button type="button" disabled={!manualDesc.trim() || addLine.isPending}
            onClick={() => addLine.mutate(
              { option_id: o.id, kind: "custom", description: manualDesc.trim(), unit_price: manualAmt === "" ? 0 : Number(manualAmt), sort: nextSort },
              { onSuccess: () => { setManualDesc(""); setManualAmt(""); } },
            )}
            className="rounded-lg bg-brand-700 px-3.5 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-brand-800 disabled:bg-brand-300">
            <Plus className="mr-0.5 inline h-4 w-4 align-[-2px]" /> Add
          </button>
        </div>
      </div>

      {/* Protect membership on accept — same block the tiered canvas has. */}
      <div className="px-4 pb-3">
        <MembershipBlock o={o} quote={quote} allCats={allCats} />
      </div>

      <div className="flex items-center justify-between border-t-2 border-slate-200 bg-slate-50/70 px-4 py-3">
        <span className="text-base font-semibold text-slate-600">Total</span>
        <span className="text-2xl font-bold tabular-nums text-slate-900">{money(total)}</span>
      </div>
    </div>
  );
}

/** PRICEBOOK-PICK-01: the part search is the shared pricebook picker — type to add,
 *  Browse for the full pricebook with details. */
function RepairPartSearch({ onPick }: { onPick: (hit: RepairItemHit) => void }) {
  return (
    <div className="pt-1">
      <PricebookPicker pickLabel="Add to Proposal" placeholder="Search a part — name, part #, model or size…" onPick={(it) => onPick(it as RepairItemHit)} />
    </div>
  );
}
